/**
 * Bulk "Import Guests" wizard: a host uploads a CSV of already-collected RSVPs
 * or of an invite list (awaiting reply). Moved out of admin.js unchanged in
 * task #844 so the corporate event page can open the same wizard.
 *
 * Where it reads its event from: on the event dashboard, admin.js's page state
 * (currentFormId, currentFormData, allResponses, loadResponsesWithAnalytics).
 * Any other page passes a context when it opens it:
 *   openImportResponsesModal({ formId, formData, getResponses, reload, paid, inviteOnly, seating, toast })
 *   - getResponses(): the loaded responses (the "Previous imports" list is built from them)
 *   - reload(): refresh the page's list after an import or an undo
 *   - paid: true hides the paid-tools upsell on the result step
 *   - inviteOnly: false hides the Invite-only switch on the guest-list option
 *   - toast(message, 'success' | 'error'): the page's own notifications
 *   - seating: true/false offers the Table column (default: the tier gates)
 * Must load BEFORE admin.js (admin.js calls wireImportResponsesButton()).
 */

let _importCtx = null;
function _impFormId() {
    if (_importCtx) return _importCtx.formId;
    return typeof currentFormId !== 'undefined' ? currentFormId : null;
}
function _impFormData() {
    if (_importCtx) return _importCtx.formData || null;
    return typeof currentFormData !== 'undefined' ? currentFormData : null;
}
function _impResponses() {
    if (_importCtx) return (typeof _importCtx.getResponses === 'function' && _importCtx.getResponses()) || [];
    return typeof allResponses !== 'undefined' ? allResponses : [];
}
async function _impReload() {
    try {
        if (_importCtx) { if (typeof _importCtx.reload === 'function') await _importCtx.reload(); return; }
        if (typeof loadResponsesWithAnalytics === 'function') await loadResponsesWithAnalytics(_impFormId());
    } catch (e) { /* non-fatal */ }
}
// Whether the Table column is offered. FileHelpers asks the tier gates, which
// only the event dashboard loads; another page says so in its context.
function _impSeatingOffered(fd) {
    if (_importCtx && typeof _importCtx.seating === 'boolean') return _importCtx.seating;
    return window.FileHelpers._seatingOffered(fd);
}
function _impWithSeating(fn) {
    if (!_importCtx || typeof _importCtx.seating !== 'boolean') return fn();
    const FH = window.FileHelpers;
    const orig = FH._seatingOffered;
    FH._seatingOffered = () => _importCtx.seating;
    try { return fn(); } finally { FH._seatingOffered = orig; }
}
// The dashboard's toasts live in languageHelpers; another page passes its own
// as ctx.toast(message, 'success' | 'error'), or failures would be silent.
function _impToast(msg, type) {
    if (_importCtx && typeof _importCtx.toast === 'function') return _importCtx.toast(msg, type);
    return window.languageHelpers?.showToast?.(msg, type);
}
function _impIsPaid() {
    if (_importCtx && _importCtx.paid === true) return true;
    return window.FeatureGates?.isPaidForm ? window.FeatureGates.isPaidForm(_impFormData() || {}) : false;
}

// ===================================================================
// Bulk import responses — a host migrating from a spreadsheet / another
// platform uploads their already-collected RSVPs. Mirrors the Add Guest
// modal conventions (getXStrings + ensure/open + insertAdjacentHTML +
// loadResponsesWithAnalytics refresh). Parsing/mapping live in
// window.FileHelpers; the write goes through POST /responses/import.
// Imported rows are NEVER hidden by the view-cap, so the preview upsells the
// organizing TOOLS, it never threatens to hide the host's own data.
// ===================================================================

// Task #863 r2 + #883: Excel files, the corrected "digits were cut" advice,
// and the guest-list fields (party members, tags), laid over each language.
// Task #935: Guest name(s) / Group name, the invitation preview card and the
// review step's checks (a "Last, First" cell, invitations with the same name).
const IMPORT_EXTRA_STRINGS = {
    en: {
        csvOnly: 'CSV or Excel (.xlsx) files. Importing the Excel file itself keeps long phone numbers whole.',
        drop: 'Drop a CSV or Excel file here, or',
        pPhonesLost: (n) => `${n} phone number${n === 1 ? ' was' : 's were'} cut short by the spreadsheet (like 9.71586E+11) and will be kept as written. Import the Excel file (.xlsx) itself instead of a CSV, or in Excel set the phone column to Number with 0 decimal places before saving as CSV.`,
        pBelowNames: (n, max) => `${n} invitation${n === 1 ? ' names' : 's name'} more people than its ${max}. The guest's form shows only that many, and the rest are never asked:`,
        belowLine: (name, listed, max, label) => `${name}: ${listed} names, ${label} ${max}`,
        belowFix: (max) => `Use the number of names as ${max}`,
        pDerivedMax: (n, max) => `${n} guest${n === 1 ? ' has' : 's have'} no answer in your file, so the number on ${n === 1 ? 'that row' : 'those rows'} becomes their ${max}: the most their personal link allows.`,
        derivedAbove: (formMax) => `Above your form's limit of ${formMax}:`,
        fMax: 'Max guests', fTags: 'Tags',
        fTagsHint: 'Groups such as family, vip. They decide which parts of the event this guest is invited to.',
        glMapHint: 'Only what you need before inviting: the name, how to reach them, and who is on the invitation.',
        pUnknownTags: (list) => `Tags this event does not use: ${list}. These guests' links will not unlock anything until a part or website event uses them.`,
        notInFile: 'not in your file',
        fGuestNames: 'Guest name(s)', fGroupName: 'Group name',
        fGuestNamesHint: 'How to add guest names\n• Separate names with semicolons (;). Commas work too.\n• Add (child) after a child’s name, e.g. Emily Smith (child).\n• Example: Sarah Smith; Michael Smith; Emily Smith (child)\n• The phone and email on the row belong to the first guest listed.\n• Two or more names: the form asks each person, with “Attending / Declining” under each name instead of your Yes / No texts.',
        fGroupNameHint: 'Who the invitation is addressed to, like "The Smith Family". It is used on envelopes and the form’s welcome; messages greet the guest names. With no guest names, guests find the invitation by it and are asked for the names of their party; with names, each guest types their own.',
        nameOrGroupReq: 'Please map the Guest name(s) or Group name column before continuing.',
        pvTitle: 'How this invitation reads', pvAddressed: 'Addressed to', pvHi: (n) => `Hi ${n}`,
        pvFind: 'Typing any of these finds this invitation:', pvNoFind: 'Add a last name or a group name so this guest can find the invitation by name.',
        pvAskNames: 'No names yet: whoever finds it is asked for the names of their party.',
        tipSurnames: 'Tip: Add phone numbers, emails, or full names for each party member so anyone in the party can easily look up their RSVP.',
        lastFirst: (cell) => `Check: "${cell}" was read as one person`, lastFirstMore: (n) => `…and ${n} more`,
        sameName: 'Warning: You have multiple guests or groups with the same name. Please add an email address or phone number for each so they can verify their identity during lookup.',
        catCol: (field, sample) => `${field}: this column repeats a few values (like “${sample}”) across many guests. Those are categories, not names: each would be saved on the invitation. Set ${field} to Ignore, or put them under Tags.`,
        addInviteesBtn: 'Add invitees', addInviteesTitle: 'Add people who haven’t replied yet: just the name, how to reach them and who is on the invitation', typeIn: 'or type them in', backToFile: 'Upload a file instead', addRow: 'Add guest', manualHint: 'Add the people you are inviting. They show as awaiting reply; nothing is sent now.', manualNeedOne: 'Add at least one name.', manualSave: 'Add to guest list', manualAdded: (n) => n === 1 ? '1 guest added to your guest list' : `${n} guests added to your guest list`, excelFail: 'Could not read that Excel file. Save it as CSV and try again.'
    },
    es: {
        csvOnly: 'Archivos CSV o Excel (.xlsx). Importar el propio Excel conserva completos los teléfonos largos.',
        drop: 'Arrastra un CSV o Excel aquí, o',
        pPhonesLost: (n) => `${n} teléfono${n === 1 ? ' quedó recortado' : 's quedaron recortados'} por la hoja de cálculo (como 9.71586E+11) y se guardará${n === 1 ? '' : 'n'} tal cual. Importa directamente el archivo Excel (.xlsx) en vez de un CSV, o en Excel pon la columna de teléfono como Número con 0 decimales antes de guardar como CSV.`,
        pBelowNames: (n, max) => `${n} invitaci${n === 1 ? 'ón nombra' : 'ones nombran'} más personas que su ${max}. Su formulario solo muestra esa cantidad y al resto nunca se le pregunta:`,
        belowLine: (name, listed, max, label) => `${name}: ${listed} nombres, ${label} ${max}`,
        belowFix: (max) => `Usar el número de nombres como ${max}`,
        pDerivedMax: (n, max) => `${n} invitado${n === 1 ? ' no tiene' : 's no tienen'} respuesta en tu archivo, así que su número pasa a ser su ${max}: lo máximo que permite su enlace personal.`,
        derivedAbove: (formMax) => `Por encima del límite de tu formulario (${formMax}):`,
        fMax: 'Máx. invitados', fTags: 'Etiquetas',
        fTagsHint: 'Grupos como familia, vip. Deciden a qué partes del evento está invitado.',
        glMapHint: 'Solo lo necesario antes de invitar: el nombre, cómo contactarlos y quién va en la invitación.',
        pUnknownTags: (list) => `Etiquetas que este evento no usa: ${list}. Los enlaces de estos invitados no desbloquearán nada hasta que una parte o evento del sitio las use.`,
        notInFile: 'no está en tu archivo',
        fGuestNames: 'Nombre(s) de invitados', fGroupName: 'Nombre del grupo',
        fGuestNamesHint: 'Cómo añadir nombres de invitados\n• Separa los nombres con punto y coma (;). Las comas también funcionan.\n• Añade (niño) después del nombre de un niño, p. ej. Lucas López (niño).\n• Ejemplo: Sara López; Miguel López; Lucas López (niño)\n• El teléfono y el correo de la fila son de la primera persona de la lista.\n• Con dos o más nombres, el formulario pregunta a cada persona, con «Asiste / No asiste» bajo cada nombre en lugar de tus textos de Sí / No.',
        fGroupNameHint: 'A quién va dirigida la invitación, como "Familia Pérez". Se usa en sobres y en la bienvenida del formulario; los mensajes saludan a los nombres de los invitados. Sin nombres de invitados, los invitados encuentran la invitación con él y se les piden los nombres de su grupo; con nombres, cada invitado escribe el suyo.',
        nameOrGroupReq: 'Asigna la columna Nombre(s) de invitados o Nombre del grupo antes de continuar.',
        pvTitle: 'Así se verá esta invitación', pvAddressed: 'Dirigida a', pvHi: (n) => `Hola, ${n}`,
        pvFind: 'Escribiendo cualquiera de estos se encuentra la invitación:', pvNoFind: 'Añade un apellido o un nombre de grupo para que este invitado pueda encontrar la invitación por su nombre.',
        pvAskNames: 'Aún sin nombres: a quien la encuentre se le pedirán los nombres de su grupo.',
        tipSurnames: 'Consejo: añade teléfonos, correos o nombres completos para cada miembro del grupo, así cualquiera de ellos podrá encontrar fácilmente su RSVP.',
        lastFirst: (cell) => `Revisa: "${cell}" se leyó como una sola persona`, lastFirstMore: (n) => `…y ${n} más`,
        sameName: 'Atención: tienes varios invitados o grupos con el mismo nombre. Añade un correo o un teléfono para cada uno, así podrán confirmar su identidad al buscar su invitación.',
        catCol: (field, sample) => `${field}: esta columna repite unos pocos valores (como «${sample}») en muchos invitados. Son categorías, no nombres: cada uno se guardaría en la invitación. Pon ${field} en Ignorar o ponlos en Etiquetas.`,
        addInviteesBtn: 'Añadir invitados', addInviteesTitle: 'Añade personas que aún no han respondido: solo el nombre, cómo contactarlas y quién va en la invitación', typeIn: 'o escríbelos aquí', backToFile: 'Subir un archivo', addRow: 'Añadir invitado', manualHint: 'Añade a las personas que invitas. Aparecen como pendientes de respuesta; no se envía nada ahora.', manualNeedOne: 'Añade al menos un nombre.', manualSave: 'Añadir a la lista', manualAdded: (n) => n === 1 ? '1 invitado añadido a tu lista' : `${n} invitados añadidos a tu lista`, excelFail: 'No se pudo leer ese Excel. Guárdalo como CSV e inténtalo de nuevo.'
    },
    fr: {
        csvOnly: 'Fichiers CSV ou Excel (.xlsx). Importer le fichier Excel lui-même garde les longs numéros entiers.',
        drop: 'Déposez un CSV ou un Excel ici, ou',
        pPhonesLost: (n) => `${n} numéro${n === 1 ? ' a été tronqué' : 's ont été tronqués'} par le tableur (comme 9.71586E+11) et ${n === 1 ? 'sera gardé' : 'seront gardés'} tel quel. Importez directement le fichier Excel (.xlsx) au lieu d’un CSV, ou dans Excel mettez la colonne téléphone en Nombre à 0 décimale avant d’enregistrer en CSV.`,
        pBelowNames: (n, max) => `${n} invitation${n === 1 ? ' nomme' : 's nomment'} plus de personnes que son ${max}. Le formulaire n’en affiche que ce nombre ; les autres ne sont jamais invités à répondre :`,
        belowLine: (name, listed, max, label) => `${name} : ${listed} noms, ${label} ${max}`,
        belowFix: (max) => `Prendre le nombre de noms comme ${max}`,
        pDerivedMax: (n, max) => `${n} invité${n === 1 ? ' n’a' : 's n’ont'} pas de réponse dans votre fichier : leur nombre devient leur ${max}, le maximum autorisé par leur lien personnel.`,
        derivedAbove: (formMax) => `Au-dessus de la limite de votre formulaire (${formMax}) :`,
        fMax: 'Invités max.', fTags: 'Étiquettes',
        fTagsHint: 'Groupes comme famille, vip. Ils décident à quelles parties de l’événement l’invité est convié.',
        glMapHint: 'Seulement l’essentiel avant d’inviter : le nom, comment les joindre et qui figure sur l’invitation.',
        pUnknownTags: (list) => `Étiquettes que cet événement n’utilise pas : ${list}. Les liens de ces invités ne débloqueront rien tant qu’aucune partie ou événement du site ne les utilise.`,
        notInFile: 'absent de votre fichier',
        fGuestNames: 'Nom(s) des invités', fGroupName: 'Nom du groupe',
        fGuestNamesHint: 'Comment ajouter les noms des invités\n• Séparez les noms par un point-virgule (;). Les virgules fonctionnent aussi.\n• Ajoutez (enfant) après le nom d’un enfant, par ex. Léo Martin (enfant).\n• Exemple : Sophie Martin; Marc Martin; Léo Martin (enfant)\n• Le téléphone et l’e-mail de la ligne sont ceux de la première personne de la liste.\n• Avec deux noms ou plus, le formulaire interroge chaque personne, avec « Présent / Absent » sous chaque nom au lieu de vos textes Oui / Non.',
        fGroupNameHint: 'À qui l’invitation est adressée, par exemple « Famille Martin ». Il figure sur les enveloppes et dans l’accueil du formulaire ; les messages saluent les noms des invités. Sans noms d’invités, les invités retrouvent l’invitation avec ce nom et indiquent les noms de leur groupe ; avec des noms, chaque invité saisit le sien.',
        nameOrGroupReq: 'Associez la colonne Nom(s) des invités ou Nom du groupe avant de continuer.',
        pvTitle: 'Aperçu de cette invitation', pvAddressed: 'Adressée à', pvHi: (n) => `Bonjour ${n}`,
        pvFind: 'Saisir l’un de ces noms retrouve cette invitation :', pvNoFind: 'Ajoutez un nom de famille ou un nom de groupe pour que cet invité retrouve l’invitation par son nom.',
        pvAskNames: 'Pas encore de noms : la personne qui la trouve indiquera les noms de son groupe.',
        tipSurnames: 'Astuce : ajoutez des numéros de téléphone, des e-mails ou des noms complets pour chaque membre du groupe, afin que chacun puisse facilement retrouver son RSVP.',
        lastFirst: (cell) => `À vérifier : « ${cell} » a été lu comme une seule personne`, lastFirstMore: (n) => `…et ${n} de plus`,
        sameName: 'Attention : plusieurs invités ou groupes portent le même nom. Ajoutez une adresse e-mail ou un numéro de téléphone pour chacun afin qu’ils puissent confirmer leur identité lors de la recherche.',
        catCol: (field, sample) => `${field} : cette colonne répète quelques valeurs (comme « ${sample} ») pour de nombreux invités. Ce sont des catégories, pas des noms : chacune serait enregistrée sur l’invitation. Réglez ${field} sur Ignorer, ou mettez-les dans Étiquettes.`,
        addInviteesBtn: 'Ajouter des invités', addInviteesTitle: 'Ajoutez des personnes qui n’ont pas encore répondu : le nom, comment les joindre et qui figure sur l’invitation', typeIn: 'ou saisissez-les ici', backToFile: 'Importer un fichier', addRow: 'Ajouter un invité', manualHint: 'Ajoutez les personnes que vous invitez. Elles apparaissent en attente de réponse ; rien n’est envoyé maintenant.', manualNeedOne: 'Ajoutez au moins un nom.', manualSave: 'Ajouter à la liste', manualAdded: (n) => n === 1 ? '1 invité ajouté à votre liste' : `${n} invités ajoutés à votre liste`, excelFail: 'Impossible de lire ce fichier Excel. Enregistrez-le en CSV et réessayez.'
    },
    de: {
        csvOnly: 'CSV- oder Excel-Dateien (.xlsx). Wenn Sie die Excel-Datei selbst importieren, bleiben lange Telefonnummern vollständig.',
        drop: 'CSV- oder Excel-Datei hier ablegen, oder',
        pPhonesLost: (n) => `${n} Telefonnummer${n === 1 ? ' wurde' : 'n wurden'} von der Tabelle gekürzt (wie 9.71586E+11) und ${n === 1 ? 'bleibt' : 'bleiben'} wie geschrieben. Importieren Sie direkt die Excel-Datei (.xlsx) statt einer CSV, oder stellen Sie in Excel die Telefonspalte auf Zahl mit 0 Dezimalstellen, bevor Sie als CSV speichern.`,
        pBelowNames: (n, max) => `${n} Einladung${n === 1 ? ' nennt' : 'en nennen'} mehr Personen als ihre ${max}. Das Formular zeigt nur so viele; die übrigen werden nie gefragt:`,
        belowLine: (name, listed, max, label) => `${name}: ${listed} Namen, ${label} ${max}`,
        belowFix: (max) => `Anzahl der Namen als ${max} verwenden`,
        pDerivedMax: (n, max) => `${n === 1 ? '1 Gast hat' : n + ' Gäste haben'} in Ihrer Datei keine Antwort, daher wird die Zahl zur Angabe „${max}“: so viele erlaubt der persönliche Link höchstens.`,
        derivedAbove: (formMax) => `Über dem Limit Ihres Formulars (${formMax}):`,
        fMax: 'Max. Gäste', fTags: 'Tags',
        fTagsHint: 'Gruppen wie Familie, VIP. Sie bestimmen, zu welchen Programmpunkten der Gast eingeladen ist.',
        glMapHint: 'Nur was Sie vor dem Einladen brauchen: Name, Erreichbarkeit und wer auf der Einladung steht.',
        pUnknownTags: (list) => `Tags, die diese Veranstaltung nicht verwendet: ${list}. Die Links dieser Gäste schalten nichts frei, bis ein Programmpunkt oder Website-Event sie verwendet.`,
        notInFile: 'nicht in Ihrer Datei',
        fGuestNames: 'Gastname(n)', fGroupName: 'Gruppenname',
        fGuestNamesHint: 'So tragen Sie Gastnamen ein\n• Trennen Sie Namen mit einem Semikolon (;). Kommas gehen auch.\n• Schreiben Sie (Kind) hinter den Namen eines Kindes, z. B. Leon Schmidt (Kind).\n• Beispiel: Anna Schmidt; Max Schmidt; Leon Schmidt (Kind)\n• Telefon und E-Mail der Zeile gehören zur ersten genannten Person.\n• Bei zwei oder mehr Namen fragt das Formular jede Person einzeln, mit „Kommt / Kommt nicht“ unter jedem Namen statt Ihrer Ja-/Nein-Texte.',
        fGroupNameHint: 'An wen die Einladung gerichtet ist, z. B. „Familie Müller“. Er steht auf Umschlägen und in der Begrüßung des Formulars; Nachrichten sprechen die Gastnamen an. Ohne Gastnamen finden Gäste die Einladung darüber und werden gefragt, wer zur Gruppe gehört; mit Namen gibt jeder Gast seinen eigenen ein.',
        nameOrGroupReq: 'Bitte ordnen Sie die Spalte Gastname(n) oder Gruppenname zu, bevor Sie fortfahren.',
        pvTitle: 'So erscheint diese Einladung', pvAddressed: 'Adressiert an', pvHi: (n) => `Hallo ${n}`,
        pvFind: 'Wer einen dieser Namen eingibt, findet diese Einladung:', pvNoFind: 'Fügen Sie einen Nachnamen oder Gruppennamen hinzu, damit dieser Gast die Einladung über seinen Namen findet.',
        pvAskNames: 'Noch keine Namen: Wer sie findet, wird nach den Namen seiner Gruppe gefragt.',
        tipSurnames: 'Tipp: Fügen Sie für jedes Gruppenmitglied Telefonnummer, E-Mail oder den vollständigen Namen hinzu, damit jeder in der Gruppe seine Antwort leicht findet.',
        lastFirst: (cell) => `Prüfen: „${cell}“ wurde als eine Person gelesen`, lastFirstMore: (n) => `…und ${n} weitere`,
        sameName: 'Achtung: Mehrere Gäste oder Gruppen haben denselben Namen. Fügen Sie für jeden eine E-Mail-Adresse oder Telefonnummer hinzu, damit sie sich bei der Suche ausweisen können.',
        catCol: (field, sample) => `${field}: Diese Spalte wiederholt wenige Werte (wie „${sample}“) bei vielen Gästen. Das sind Kategorien, keine Namen: Jeder Wert würde in der Einladung gespeichert. Stellen Sie ${field} auf Ignorieren oder tragen Sie sie unter Tags ein.`,
        addInviteesBtn: 'Eingeladene hinzufügen', addInviteesTitle: 'Personen hinzufügen, die noch nicht geantwortet haben: nur Name, Erreichbarkeit und wer auf der Einladung steht', typeIn: 'oder hier eintippen', backToFile: 'Stattdessen Datei hochladen', addRow: 'Gast hinzufügen', manualHint: 'Fügen Sie die Personen hinzu, die Sie einladen. Sie erscheinen als „Antwort ausstehend“; jetzt wird nichts gesendet.', manualNeedOne: 'Fügen Sie mindestens einen Namen hinzu.', manualSave: 'Zur Gästeliste hinzufügen', manualAdded: (n) => n === 1 ? '1 Gast zur Gästeliste hinzugefügt' : `${n} Gäste zur Gästeliste hinzugefügt`, excelFail: 'Diese Excel-Datei konnte nicht gelesen werden. Speichern Sie sie als CSV und versuchen Sie es erneut.'
    },
    pt: {
        csvOnly: 'Arquivos CSV ou Excel (.xlsx). Importar o próprio Excel mantém os telefones longos inteiros.',
        drop: 'Solte um CSV ou Excel aqui, ou',
        pPhonesLost: (n) => `${n} telefone${n === 1 ? ' foi cortado' : 's foram cortados'} pela planilha (como 9.71586E+11) e ${n === 1 ? 'será mantido' : 'serão mantidos'} como escrito. Importe direto o arquivo Excel (.xlsx) em vez de um CSV, ou no Excel defina a coluna de telefone como Número com 0 casas decimais antes de salvar como CSV.`,
        pBelowNames: (n, max) => `${n} convite${n === 1 ? ' nomeia' : 's nomeiam'} mais pessoas do que o seu ${max}. O formulário só mostra esse número e os restantes nunca são perguntados:`,
        belowLine: (name, listed, max, label) => `${name}: ${listed} nomes, ${label} ${max}`,
        belowFix: (max) => `Usar o número de nomes como ${max}`,
        pDerivedMax: (n, max) => `${n} convidado${n === 1 ? ' não tem' : 's não têm'} resposta no seu arquivo, então o número passa a ser o ${max}: o máximo que o link pessoal permite.`,
        derivedAbove: (formMax) => `Acima do limite do seu formulário (${formMax}):`,
        fMax: 'Máx. convidados', fTags: 'Etiquetas',
        fTagsHint: 'Grupos como família, vip. Decidem para quais partes do evento o convidado é chamado.',
        glMapHint: 'Só o necessário antes de convidar: o nome, como contatar e quem está no convite.',
        pUnknownTags: (list) => `Etiquetas que este evento não usa: ${list}. Os links desses convidados não liberam nada até que uma parte ou evento do site as use.`,
        notInFile: 'não está no seu arquivo',
        fGuestNames: 'Nome(s) dos convidados', fGroupName: 'Nome do grupo',
        fGuestNamesHint: 'Como adicionar nomes de convidados\n• Separe os nomes com ponto e vírgula (;). Vírgulas também funcionam.\n• Acrescente (criança) após o nome de uma criança, p. ex. Pedro Silva (criança).\n• Exemplo: Ana Silva; João Silva; Pedro Silva (criança)\n• O telefone e o e-mail da linha são da primeira pessoa da lista.\n• Com dois ou mais nomes, o formulário pergunta a cada pessoa, com «Vai / Não vai» sob cada nome em vez dos seus textos de Sim / Não.',
        fGroupNameHint: 'A quem o convite é endereçado, como "Família Silva". Aparece nos envelopes e nas boas-vindas do formulário; as mensagens saúdam os nomes dos convidados. Sem nomes de convidados, os convidados encontram o convite por ele e informam os nomes do seu grupo; com nomes, cada convidado digita o seu.',
        nameOrGroupReq: 'Mapeie a coluna Nome(s) dos convidados ou Nome do grupo antes de continuar.',
        pvTitle: 'Como este convite aparece', pvAddressed: 'Endereçado a', pvHi: (n) => `Olá, ${n}`,
        pvFind: 'Digitar qualquer um destes encontra este convite:', pvNoFind: 'Adicione um sobrenome ou um nome de grupo para que este convidado encontre o convite pelo nome.',
        pvAskNames: 'Ainda sem nomes: quem encontrar o convite informará os nomes do seu grupo.',
        tipSurnames: 'Dica: adicione telefones, e-mails ou nomes completos para cada membro do grupo, assim qualquer um deles encontra facilmente o seu RSVP.',
        lastFirst: (cell) => `Verifique: "${cell}" foi lido como uma só pessoa`, lastFirstMore: (n) => `…e mais ${n}`,
        sameName: 'Atenção: você tem vários convidados ou grupos com o mesmo nome. Adicione um e-mail ou telefone para cada um, para que possam confirmar a identidade ao buscar o convite.',
        catCol: (field, sample) => `${field}: esta coluna repete poucos valores (como “${sample}”) em muitos convidados. São categorias, não nomes: cada um seria salvo no convite. Defina ${field} como Ignorar ou coloque-os em Etiquetas.`,
        addInviteesBtn: 'Adicionar convidados', addInviteesTitle: 'Adicione pessoas que ainda não responderam: só o nome, como contatar e quem está no convite', typeIn: 'ou digite-os aqui', backToFile: 'Enviar um arquivo', addRow: 'Adicionar convidado', manualHint: 'Adicione as pessoas que você está convidando. Elas aparecem aguardando resposta; nada é enviado agora.', manualNeedOne: 'Adicione pelo menos um nome.', manualSave: 'Adicionar à lista', manualAdded: (n) => n === 1 ? '1 convidado adicionado à sua lista' : `${n} convidados adicionados à sua lista`, excelFail: 'Não foi possível ler esse Excel. Salve-o como CSV e tente de novo.'
    },
    he: {
        csvOnly: 'קבצי CSV או Excel ‏(.xlsx). ייבוא קובץ ה-Excel עצמו שומר על מספרי טלפון ארוכים במלואם.',
        drop: 'גררו קובץ CSV או Excel לכאן, או',
        pPhonesLost: (n) => `${n} מספרי טלפון קוצרו על ידי הגיליון (כמו 9.71586E+11) וישמרו כפי שנכתבו. ייבאו ישירות את קובץ ה-Excel ‏(.xlsx) במקום CSV, או ב-Excel הגדירו את עמודת הטלפון כ"מספר" בלי ספרות אחרי הנקודה לפני השמירה כ-CSV.`,
        pBelowNames: (n, max) => `${n === 1 ? 'הזמנה אחת מונה' : n + ' הזמנות מונות'} יותר אנשים מ${max}. בטופס יופיע רק המספר הזה, והשאר לא יישאלו:`,
        belowLine: (name, listed, max, label) => `${name}: ${listed} שמות, ${label} ${max}`,
        belowFix: (max) => `לקבוע את ${max} לפי מספר השמות`,
        pDerivedMax: (n, max) => `${n === 1 ? 'לאורח אחד' : 'ל-' + n + ' אורחים'} אין תשובה בקובץ, ולכן המספר בשורה ייקבע כ${max}: המקסימום שהקישור האישי מאפשר.`,
        derivedAbove: (formMax) => `מעל המגבלה של הטופס (${formMax}):`,
        fMax: 'מקסימום אורחים', fTags: 'תגיות',
        fTagsHint: 'קבוצות כמו משפחה, VIP. הן קובעות לאילו חלקים באירוע האורח מוזמן.',
        glMapHint: 'רק מה שצריך לפני ההזמנה: שם, איך ליצור קשר ומי בהזמנה.',
        pUnknownTags: (list) => `תגיות שהאירוע לא משתמש בהן: ${list}. הקישורים של האורחים האלה לא יפתחו כלום עד שחלק באירוע או אירוע באתר ישתמש בהן.`,
        notInFile: 'לא בקובץ שלכם',
        fGuestNames: 'שמות האורחים', fGroupName: 'שם הקבוצה',
        fGuestNamesHint: 'איך מוסיפים שמות אורחים\n• מפרידים בין שמות בנקודה-פסיק (;). אפשר גם בפסיק.\n• מוסיפים (ילד) אחרי שם של ילד, למשל: יואב כהן (ילד).\n• דוגמה: שרה כהן; מיכאל כהן; יואב כהן (ילד)\n• הטלפון והאימייל בשורה שייכים לאורח הראשון ברשימה.\n• שני שמות או יותר: הטופס שואל כל אחד בנפרד, עם ״מגיע/ה / לא מגיע/ה״ מתחת לכל שם, במקום טקסטי ה״כן / לא״ שלך.',
        fGroupNameHint: 'למי ההזמנה מיועדת, למשל "משפחת כהן". השם מופיע על מעטפות ובברכת הטופס; ההודעות פונות לשמות האורחים. בלי שמות אורחים, האורחים מוצאים את ההזמנה לפיו ומתבקשים למלא את שמות בני הקבוצה; עם שמות, כל אורח מקליד את השם שלו.',
        nameOrGroupReq: 'יש למפות את עמודת שמות האורחים או שם הקבוצה לפני שממשיכים.',
        pvTitle: 'כך תיראה ההזמנה', pvAddressed: 'נמען:', pvHi: (n) => `היי ${n}`,
        pvFind: 'הקלדה של כל אחד מאלה תמצא את ההזמנה:', pvNoFind: 'הוסיפו שם משפחה או שם קבוצה כדי שהאורח יוכל למצוא את ההזמנה לפי שם.',
        pvAskNames: 'עדיין אין שמות: מי שימצא את ההזמנה יתבקש למלא את שמות בני הקבוצה.',
        tipSurnames: 'טיפ: הוסיפו מספרי טלפון, אימיילים או שמות מלאים לכל אחד מבני הקבוצה, כדי שכל אחד מהם יוכל למצוא בקלות את אישור ההגעה שלו.',
        lastFirst: (cell) => `לבדיקה: "${cell}" נקרא כאדם אחד`, lastFirstMore: (n) => `…ועוד ${n}`,
        sameName: 'שימו לב: יש לכם כמה אורחים או קבוצות עם אותו שם. הוסיפו כתובת אימייל או מספר טלפון לכל אחד, כדי שיוכלו לאמת את זהותם בחיפוש ההזמנה.',
        catCol: (field, sample) => `${field}: העמודה הזו חוזרת על ערכים ספורים (כמו ״${sample}״) אצל אורחים רבים. אלה קטגוריות, לא שמות: כל אחד מהם יישמר בהזמנה. העבירו את ${field} ל״התעלם״, או שימו אותם בתגיות.`,
        addInviteesBtn: 'הוספת מוזמנים', addInviteesTitle: 'הוספת אנשים שעוד לא ענו: רק שם, איך ליצור קשר ומי בהזמנה', typeIn: 'או הקלידו אותם כאן', backToFile: 'העלאת קובץ במקום', addRow: 'הוספת אורח', manualHint: 'הוסיפו את האנשים שאתם מזמינים. הם יופיעו כ"ממתין לתשובה"; שום דבר לא נשלח עכשיו.', manualNeedOne: 'הוסיפו לפחות שם אחד.', manualSave: 'הוספה לרשימת המוזמנים', manualAdded: (n) => n === 1 ? 'אורח אחד נוסף לרשימת המוזמנים' : `${n} אורחים נוספו לרשימת המוזמנים`, excelFail: 'לא הצלחנו לקרוא את קובץ ה-Excel. שמרו אותו כ-CSV ונסו שוב.'
    },
    ar: {
        csvOnly: 'ملفات CSV أو Excel ‏(.xlsx). استيراد ملف Excel نفسه يحافظ على أرقام الهاتف الطويلة كاملة.',
        drop: 'أفلت ملف CSV أو Excel هنا، أو',
        pPhonesLost: (n) => `${n} رقم هاتف قصّه الجدول (مثل 9.71586E+11) وسيُحفظ كما كُتب. استورد ملف Excel ‏(.xlsx) مباشرة بدل CSV، أو في Excel اجعل عمود الهاتف "رقمًا" بلا منازل عشرية قبل الحفظ كـ CSV.`,
        pBelowNames: (n, max) => `${n === 1 ? 'دعوة واحدة تذكر' : n + ' دعوات تذكر'} أشخاصًا أكثر من ${max}. يعرض النموذج هذا العدد فقط، ولن يُسأل الباقون:`,
        belowLine: (name, listed, max, label) => `${name}: ${listed} أسماء، ${label} ${max}`,
        belowFix: (max) => `اعتماد عدد الأسماء كـ ${max}`,
        pDerivedMax: (n, max) => `${n === 1 ? 'ضيف واحد ليس له' : n + ' ضيوف ليس لهم'} رد في ملفك، لذا يصبح العدد في الصف ${max}: أقصى ما يسمح به الرابط الشخصي.`,
        derivedAbove: (formMax) => `أعلى من حد النموذج (${formMax}):`,
        fMax: 'الحد الأقصى للضيوف', fTags: 'الوسوم',
        fTagsHint: 'مجموعات مثل العائلة، VIP. تحدد أجزاء الحدث المدعو إليها الضيف.',
        glMapHint: 'فقط ما تحتاجه قبل الدعوة: الاسم، وطريقة التواصل، ومن في الدعوة.',
        pUnknownTags: (list) => `وسوم لا يستخدمها هذا الحدث: ${list}. لن تفتح روابط هؤلاء الضيوف شيئًا حتى يستخدمها جزء أو فعالية في الموقع.`,
        notInFile: 'غير موجود في ملفك',
        fGuestNames: 'أسماء الضيوف', fGroupName: 'اسم المجموعة',
        fGuestNamesHint: 'كيفية إضافة أسماء الضيوف\n• افصل بين الأسماء بفاصلة منقوطة (;). الفاصلة تعمل أيضًا.\n• أضف (طفل) بعد اسم الطفل، مثل: يوسف حداد (طفل).\n• مثال: سارة حداد; محمد حداد; يوسف حداد (طفل)\n• الهاتف والبريد في الصف للضيف الأول في القائمة.\n• مع اسمين أو أكثر، يسأل النموذج كل شخص على حدة، مع «سيحضر / لن يحضر» تحت كل اسم بدلًا من نصوص «نعم / لا» الخاصة بك.',
        fGroupNameHint: 'لمن الدعوة موجهة، مثل "عائلة الأحمد". يظهر على الأظرف وفي ترحيب النموذج؛ أما الرسائل فتحيّي أسماء الضيوف. دون أسماء ضيوف، يعثر الضيوف على الدعوة به ويُطلب منهم إدخال أسماء أفراد مجموعتهم؛ ومع الأسماء، يكتب كل ضيف اسمه.',
        nameOrGroupReq: 'يرجى مطابقة عمود أسماء الضيوف أو اسم المجموعة قبل المتابعة.',
        pvTitle: 'هكذا ستظهر هذه الدعوة', pvAddressed: 'موجهة إلى', pvHi: (n) => `مرحبًا ${n}`,
        pvFind: 'كتابة أي من هذه تعثر على الدعوة:', pvNoFind: 'أضف اسم عائلة أو اسم مجموعة ليتمكن هذا الضيف من العثور على الدعوة باسمه.',
        pvAskNames: 'لا أسماء بعد: من يعثر عليها سيُطلب منه إدخال أسماء أفراد مجموعته.',
        tipSurnames: 'نصيحة: أضف أرقام هواتف أو عناوين بريد أو أسماء كاملة لكل فرد في المجموعة، ليتمكن أي منهم من العثور على تأكيد حضوره بسهولة.',
        lastFirst: (cell) => `تحقق: قُرئ "${cell}" كشخص واحد`, lastFirstMore: (n) => `…و${n} أخرى`,
        sameName: 'تنبيه: لديك عدة ضيوف أو مجموعات بالاسم نفسه. أضف بريدًا إلكترونيًا أو رقم هاتف لكل منهم ليتمكنوا من تأكيد هويتهم عند البحث.',
        catCol: (field, sample) => `${field}: يكرر هذا العمود قيمًا قليلة (مثل «${sample}») لدى ضيوف كثيرين. هذه فئات وليست أسماء: ستُحفظ كل قيمة في الدعوة. اجعل ${field} «تجاهل»، أو ضعها في الوسوم.`,
        addInviteesBtn: 'إضافة مدعوين', addInviteesTitle: 'أضف أشخاصًا لم يردّوا بعد: الاسم وطريقة التواصل ومن في الدعوة فقط', typeIn: 'أو اكتبهم هنا', backToFile: 'رفع ملف بدلاً من ذلك', addRow: 'إضافة ضيف', manualHint: 'أضف من تدعوهم. سيظهرون بانتظار الرد؛ لا يُرسل شيء الآن.', manualNeedOne: 'أضف اسمًا واحدًا على الأقل.', manualSave: 'إضافة إلى قائمة الضيوف', manualAdded: (n) => n === 1 ? 'أُضيف ضيف واحد إلى قائمتك' : `أُضيف ${n} ضيوف إلى قائمتك`, excelFail: 'تعذّرت قراءة ملف Excel هذا. احفظه كـ CSV وحاول مجددًا.'
    }
};

function getImportStrings() {
    const lang = window.DASHBOARD_LANGUAGE || 'en';
    const strings = {
        en: {
            title: 'Import Guests', sUpload: 'Upload', sMap: 'Map', sPreview: 'Preview', sDone: 'Done',
            back: 'Back', next: 'Next', cancel: 'Cancel', importBtn: 'Import', importing: 'Importing…', close: 'Close', viewResponses: 'View responses',
            uSub: 'Upload a CSV file containing guests who already RSVP\'d or your initial guest list.',
            intentRsvps: 'Already collected RSVPs', intentRsvpsSub: 'Upload guests who have already given you their answers (a guest with no answer in the file is imported as awaiting a reply).',
            intentGuestlist: 'Guest list (no replies yet)', intentGuestlistSub: 'Upload your invite list. Guests will show as awaiting reply with their assigned party sizes.',
            ioLabel: 'Invite-only RSVP', ioDesc: 'Guests on your list (or with a personal link) RSVP up to their assigned party size; anyone else can send you a request to join, which you approve or decline.',
            ioOn: 'Invite-only RSVP is on', ioOff: 'Invite-only RSVP is off', ioFail: 'Could not change Invite-only RSVP. Please try again.',
            fGuestsAllowance: 'People per invitation',
            browse: 'choose a file', template: 'Download template', notMessaged: 'These become real responses. Your guests won\'t be messaged or emailed.', glNote: 'These guests are added as awaiting reply. Nothing is sent now: invite them by message or email from Messages when you are ready.', uSub2: 'What kind of list are you uploading?',
            noHeader: 'My file has no header row',
            detected: (n) => `Detected ${n} row${n === 1 ? '' : 's'}.`, empty: 'That file has no rows we could read.', parseErr: 'Could not read that file. Please export it as a CSV and try again.',
            mHeading: 'Match your columns', mHint: 'We matched your columns automatically — adjust anything that looks off.',
            required: 'required', ignore: '— Ignore —', nameReq: 'Please map the Name column before continuing.', preview: 'Preview',
            attHeading: 'Attendance values', attHint: 'We couldn\'t recognize these values — tell us what they mean:',
            fLastName: 'Last name', fLastNameHint: 'If the last name is in its own column, it is added after the name with a space.', fAttending: 'Attending status', fGuests: 'Number of guests', fAdults: 'Adults', fChildren: 'Children',
            fDietary: 'Meal / dietary', fRelationship: 'Relationship', fEmail: 'Email', fPhone: 'Phone', fDate: 'Response date', fCustom: 'Custom fields',
            fCompany: 'Company', fJobTitle: 'Job title', fDepartment: 'Department',
            fTable: 'Table', fTableHint: 'Seats these guests at the table you name. A table that does not exist yet is created.',
            pHeading: 'Review before importing', pPhonesFixed: (n) => `${n} phone number${n === 1 ? '' : 's'} put in international format`, pPhonesKept: (n) => `${n} phone number${n === 1 ? '' : 's'} could not be read and will be kept exactly as written`, pImport: (n) => `${n} will be imported`, pUpdate: (n) => `${n} will update existing`, pSkip: (n) => `${n} duplicate${n === 1 ? '' : 's'} skipped`, pInvalid: (n) => `${n} skipped (no name)`,
            mode: 'Duplicates', modeSkip: 'Skip duplicates', modeUpdate: 'Update existing', modeAll: 'Import everything', running: 'Checking your file…',
            toolsTitle: 'Organize all your guests', toolsBody: 'Seating charts, check-in, and full stats are on paid plans — upgrade to organize your whole guest list.', upgrade: 'See plans',
            rHeading: 'Import complete', rImported: (n) => `${n} imported`, rUpdated: (n) => `${n} updated`, rSkipped: (n) => `${n} skipped`, rInvalid: (n) => `${n} invalid`,
            rSeated: (n, c) => `${n} seated${c ? ` (${c} new table${c === 1 ? '' : 's'})` : ''}`,
            rSeatFailed: (n) => `${n} could not be seated`,
            rSeatOver: (name, n, cap) => `${name} now has ${n} guests but seats ${cap}`,
            undo: 'Undo this import', undoing: 'Undoing…', undone: 'Import undone.', undoFail: 'Could not undo the import.',
            prevHeading: 'Previous imports', prevRemove: 'Remove', prevRemoving: 'Removing…',
            prevGuests: (n) => `${n} imported guest${n === 1 ? '' : 's'}`, prevKept: (n) => `${n} already replied — kept`,
            prevConfirm: (n) => `Remove ${n} imported guest${n === 1 ? '' : 's'} from this event? Guests who already replied are kept. This cannot be undone.`,
            prevRemoved: 'Imported guests removed.', prevFail: 'Could not remove that import.',
            failed: 'Import failed', partial: 'Some rows were saved before an error stopped the import. You can undo them below.',
            noFormSelected: 'Please select an event first', yes: 'Yes', no: 'No', maybe: 'Maybe', pending: 'Awaiting reply', skipVal: 'Skip these'
        },
        es: {
            title: 'Importar invitados', sUpload: 'Subir', sMap: 'Asignar', sPreview: 'Vista previa', sDone: 'Listo',
            back: 'Atrás', next: 'Siguiente', cancel: 'Cancelar', importBtn: 'Importar', importing: 'Importando…', close: 'Cerrar', viewResponses: 'Ver respuestas',
            uSub: 'Sube un archivo CSV con los invitados que ya confirmaron o con tu lista de invitados inicial.',
            intentRsvps: 'Confirmaciones ya recibidas', intentRsvpsSub: 'Sube a los invitados que ya te respondieron (un invitado sin respuesta en el archivo se importa como pendiente de respuesta).',
            intentGuestlist: 'Lista de invitados (sin respuestas aún)', intentGuestlistSub: 'Sube tu lista de invitados. Aparecerán como pendientes de respuesta, con el número de personas asignado.',
            ioLabel: 'RSVP solo con invitación', ioDesc: 'Los invitados de tu lista (o con un enlace personal) confirman hasta el número de personas asignado; cualquier otra persona puede enviarte una solicitud para unirse, que apruebas o rechazas.',
            ioOn: 'RSVP solo con invitación activado', ioOff: 'RSVP solo con invitación desactivado', ioFail: 'No se pudo cambiar el RSVP solo con invitación. Inténtalo de nuevo.',
            fGuestsAllowance: 'Personas por invitación',
            browse: 'elige un archivo', template: 'Descargar plantilla', notMessaged: 'Se convierten en respuestas reales. No se enviará ningún mensaje ni correo a tus invitados.', glNote: 'Estos invitados se añaden como pendientes de respuesta. Ahora no se envía nada: invítalos por mensaje o correo desde Mensajes cuando quieras.', uSub2: '¿Qué tipo de lista vas a subir?',
            noHeader: 'Mi archivo no tiene fila de encabezado',
            detected: (n) => `Se detectaron ${n} fila${n === 1 ? '' : 's'}.`, empty: 'Ese archivo no tiene filas legibles.', parseErr: 'No se pudo leer el archivo. Expórtalo como CSV e inténtalo de nuevo.',
            mHeading: 'Asigna tus columnas', mHint: 'Asignamos tus columnas automáticamente — ajusta lo que sea necesario.',
            required: 'obligatorio', ignore: '— Ignorar —', nameReq: 'Asigna la columna de Nombre antes de continuar.', preview: 'Vista previa',
            attHeading: 'Valores de asistencia', attHint: 'No reconocimos estos valores — indícanos qué significan:',
            fLastName: 'Apellido', fLastNameHint: 'Si el apellido está en su propia columna, se añade después del nombre con un espacio.', fAttending: 'Asistencia', fGuests: 'Número de invitados', fAdults: 'Adultos', fChildren: 'Niños',
            fDietary: 'Comida / dieta', fRelationship: 'Relación', fEmail: 'Correo', fPhone: 'Teléfono', fDate: 'Fecha de respuesta', fCustom: 'Campos personalizados',
            fCompany: 'Empresa', fJobTitle: 'Cargo', fDepartment: 'Departamento',
            fTable: 'Mesa', fTableHint: 'Sienta a estos invitados en la mesa que indiques. Si la mesa no existe, se crea.',
            pHeading: 'Revisa antes de importar', pPhonesFixed: (n) => `${n} teléfono${n === 1 ? '' : 's'} en formato internacional`, pPhonesKept: (n) => `${n} teléfono${n === 1 ? '' : 's'} no se pudo leer y se guardará tal cual`, pImport: (n) => `${n} se importarán`, pUpdate: (n) => `${n} actualizarán existentes`, pSkip: (n) => `${n} duplicado${n === 1 ? '' : 's'} omitido${n === 1 ? '' : 's'}`, pInvalid: (n) => `${n} omitido${n === 1 ? '' : 's'} (sin nombre)`,
            mode: 'Duplicados', modeSkip: 'Omitir duplicados', modeUpdate: 'Actualizar existentes', modeAll: 'Importar todo', running: 'Revisando tu archivo…',
            toolsTitle: 'Organiza a todos tus invitados', toolsBody: 'La asignación de mesas, el check-in y las estadísticas completas están en los planes de pago — actualiza para organizar toda tu lista.', upgrade: 'Ver planes',
            rHeading: 'Importación completa', rImported: (n) => `${n} importadas`, rUpdated: (n) => `${n} actualizadas`, rSkipped: (n) => `${n} omitidas`, rInvalid: (n) => `${n} inválidas`,
            rSeated: (n, c) => `${n} sentados${c ? ` (${c} mesa${c === 1 ? '' : 's'} nueva${c === 1 ? '' : 's'})` : ''}`,
            rSeatFailed: (n) => `${n} no se pudieron sentar`,
            rSeatOver: (name, n, cap) => `${name} tiene ahora ${n} invitados pero es para ${cap}`,
            undo: 'Deshacer esta importación', undoing: 'Deshaciendo…', undone: 'Importación deshecha.', undoFail: 'No se pudo deshacer la importación.',
            prevHeading: 'Importaciones anteriores', prevRemove: 'Eliminar', prevRemoving: 'Eliminando…',
            prevGuests: (n) => `${n} invitado${n === 1 ? '' : 's'} importado${n === 1 ? '' : 's'}`, prevKept: (n) => `${n} ya respondi${n === 1 ? 'ó' : 'eron'} — se conserva${n === 1 ? '' : 'n'}`,
            prevConfirm: (n) => `¿Eliminar ${n} invitado${n === 1 ? '' : 's'} importado${n === 1 ? '' : 's'} de este evento? Se conservan los que ya respondieron. Esto no se puede deshacer.`,
            prevRemoved: 'Invitados importados eliminados.', prevFail: 'No se pudo eliminar esa importación.',
            failed: 'La importación falló', partial: 'Algunas filas se guardaron antes de un error. Puedes deshacerlas abajo.',
            noFormSelected: 'Selecciona un evento primero', yes: 'Sí', no: 'No', maybe: 'Quizás', pending: 'Sin responder', skipVal: 'Omitir estas'
        },
        fr: {
            title: 'Importer des invités', sUpload: 'Fichier', sMap: 'Associer', sPreview: 'Aperçu', sDone: 'Terminé',
            back: 'Retour', next: 'Suivant', cancel: 'Annuler', importBtn: 'Importer', importing: 'Importation…', close: 'Fermer', viewResponses: 'Voir les réponses',
            uSub: 'Importez un fichier CSV avec les invités qui ont déjà répondu ou votre liste d’invités initiale.',
            intentRsvps: 'Réponses déjà reçues', intentRsvpsSub: 'Importez les invités qui vous ont déjà répondu (un invité sans réponse dans le fichier est importé comme en attente de réponse).',
            intentGuestlist: 'Liste d’invités (pas encore de réponses)', intentGuestlistSub: 'Importez votre liste d’invités. Ils apparaîtront en attente de réponse, avec le nombre de personnes attribué.',
            ioLabel: 'RSVP sur invitation', ioDesc: 'Les invités de votre liste (ou disposant d’un lien personnel) répondent dans la limite du nombre de personnes attribué ; toute autre personne peut vous envoyer une demande de participation, que vous approuvez ou refusez.',
            ioOn: 'RSVP sur invitation activé', ioOff: 'RSVP sur invitation désactivé', ioFail: 'Impossible de modifier le RSVP sur invitation. Veuillez réessayer.',
            fGuestsAllowance: 'Personnes par invitation',
            browse: 'choisissez un fichier', template: 'Télécharger le modèle', notMessaged: 'Elles deviennent de vraies réponses. Vos invités ne recevront aucun message ni e-mail.', glNote: 'Ces invités sont ajoutés en attente de réponse. Rien n’est envoyé maintenant : invitez-les par message ou e-mail depuis Messages quand vous le souhaitez.', uSub2: 'Quel type de liste importez-vous ?',
            noHeader: 'Mon fichier n\'a pas de ligne d\'en-tête',
            detected: (n) => `${n} ligne${n === 1 ? '' : 's'} détectée${n === 1 ? '' : 's'}.`, empty: 'Ce fichier ne contient aucune ligne lisible.', parseErr: 'Impossible de lire ce fichier. Exportez-le en CSV et réessayez.',
            mHeading: 'Associez vos colonnes', mHint: 'Nous avons associé vos colonnes automatiquement — ajustez si besoin.',
            required: 'requis', ignore: '— Ignorer —', nameReq: 'Associez la colonne Nom avant de continuer.', preview: 'Aperçu',
            attHeading: 'Valeurs de présence', attHint: 'Nous n\'avons pas reconnu ces valeurs — précisez leur sens :',
            fLastName: 'Nom de famille', fLastNameHint: 'Si le nom de famille est dans une colonne à part, il est ajouté après le nom, séparé par un espace.', fAttending: 'Présence', fGuests: 'Nombre d\'invités', fAdults: 'Adultes', fChildren: 'Enfants',
            fDietary: 'Repas / régime', fRelationship: 'Relation', fEmail: 'E-mail', fPhone: 'Téléphone', fDate: 'Date de réponse', fCustom: 'Champs personnalisés',
            fCompany: 'Entreprise', fJobTitle: 'Fonction', fDepartment: 'Service',
            fTable: 'Table', fTableHint: 'Place ces invités à la table que vous indiquez. Une table inexistante est créée.',
            pHeading: 'Vérifiez avant d\'importer', pPhonesFixed: (n) => `${n} numéro${n === 1 ? '' : 's'} mis au format international`, pPhonesKept: (n) => `${n} numéro${n === 1 ? '' : 's'} illisible${n === 1 ? '' : 's'}, conservé${n === 1 ? '' : 's'} tel${n === 1 ? '' : 's'} quel${n === 1 ? '' : 's'}`, pImport: (n) => `${n} seront importées`, pUpdate: (n) => `${n} mettront à jour`, pSkip: (n) => `${n} doublon${n === 1 ? '' : 's'} ignoré${n === 1 ? '' : 's'}`, pInvalid: (n) => `${n} ignorée${n === 1 ? '' : 's'} (sans nom)`,
            mode: 'Doublons', modeSkip: 'Ignorer les doublons', modeUpdate: 'Mettre à jour', modeAll: 'Tout importer', running: 'Vérification du fichier…',
            toolsTitle: 'Organisez tous vos invités', toolsBody: 'Le plan de table, le check-in et les statistiques complètes sont dans les offres payantes — passez à un plan supérieur pour organiser toute votre liste.', upgrade: 'Voir les offres',
            rHeading: 'Importation terminée', rImported: (n) => `${n} importées`, rUpdated: (n) => `${n} mises à jour`, rSkipped: (n) => `${n} ignorées`, rInvalid: (n) => `${n} invalides`,
            rSeated: (n, c) => `${n} placés${c ? ` (${c} nouvelle${c === 1 ? '' : 's'} table${c === 1 ? '' : 's'})` : ''}`,
            rSeatFailed: (n) => `${n} n'ont pas pu être placés`,
            rSeatOver: (name, n, cap) => `${name} a maintenant ${n} invités pour ${cap} places`,
            undo: 'Annuler cet import', undoing: 'Annulation…', undone: 'Import annulé.', undoFail: 'Impossible d\'annuler l\'import.',
            prevHeading: 'Imports précédents', prevRemove: 'Supprimer', prevRemoving: 'Suppression…',
            prevGuests: (n) => `${n} invité${n === 1 ? '' : 's'} importé${n === 1 ? '' : 's'}`, prevKept: (n) => `${n} ayant déjà répondu — conservé${n === 1 ? '' : 's'}`,
            prevConfirm: (n) => `Supprimer ${n} invité${n === 1 ? '' : 's'} importé${n === 1 ? '' : 's'} de cet événement ? Les invités ayant déjà répondu sont conservés. Cette action est irréversible.`,
            prevRemoved: 'Invités importés supprimés.', prevFail: 'Impossible de supprimer cet import.',
            failed: 'Échec de l\'import', partial: 'Certaines lignes ont été enregistrées avant une erreur. Vous pouvez les annuler ci-dessous.',
            noFormSelected: 'Sélectionnez d\'abord un événement', yes: 'Oui', no: 'Non', maybe: 'Peut-être', pending: 'Sans réponse', skipVal: 'Ignorer'
        },
        de: {
            title: 'Gäste importieren', sUpload: 'Datei', sMap: 'Zuordnen', sPreview: 'Vorschau', sDone: 'Fertig',
            back: 'Zurück', next: 'Weiter', cancel: 'Abbrechen', importBtn: 'Importieren', importing: 'Importiere…', close: 'Schließen', viewResponses: 'Antworten ansehen',
            uSub: 'Laden Sie eine CSV-Datei mit Gästen hoch, die bereits geantwortet haben, oder Ihre anfängliche Gästeliste.',
            intentRsvps: 'Bereits erhaltene Antworten', intentRsvpsSub: 'Laden Sie Gäste hoch, die Ihnen bereits geantwortet haben (ein Gast ohne Antwort in der Datei wird als „Antwort ausstehend“ importiert).',
            intentGuestlist: 'Gästeliste (noch keine Antworten)', intentGuestlistSub: 'Laden Sie Ihre Einladungsliste hoch. Die Gäste erscheinen als „Antwort ausstehend“ mit ihrer zugewiesenen Personenzahl.',
            ioLabel: 'Nur auf Einladung', ioDesc: 'Gäste auf Ihrer Liste (oder mit persönlichem Link) antworten bis zu ihrer zugewiesenen Personenzahl; alle anderen können Ihnen eine Teilnahmeanfrage senden, die Sie freigeben oder ablehnen.',
            ioOn: 'Nur auf Einladung ist aktiviert', ioOff: 'Nur auf Einladung ist deaktiviert', ioFail: '„Nur auf Einladung“ konnte nicht geändert werden. Bitte erneut versuchen.',
            fGuestsAllowance: 'Personen pro Einladung',
            browse: 'Datei wählen', template: 'Vorlage herunterladen', notMessaged: 'Diese werden zu echten Antworten. Ihre Gäste erhalten keine Nachricht oder E-Mail.', glNote: 'Diese Gäste werden mit ausstehender Antwort hinzugefügt. Jetzt wird nichts gesendet: Laden Sie sie per Nachricht oder E-Mail unter Nachrichten ein, wann Sie möchten.', uSub2: 'Welche Art von Liste laden Sie hoch?',
            noHeader: 'Meine Datei hat keine Kopfzeile',
            detected: (n) => `${n} Zeile${n === 1 ? '' : 'n'} erkannt.`, empty: 'Diese Datei enthält keine lesbaren Zeilen.', parseErr: 'Datei konnte nicht gelesen werden. Bitte als CSV exportieren und erneut versuchen.',
            mHeading: 'Ordnen Sie Ihre Spalten zu', mHint: 'Wir haben Ihre Spalten automatisch zugeordnet — passen Sie bei Bedarf an.',
            required: 'erforderlich', ignore: '— Ignorieren —', nameReq: 'Bitte ordnen Sie die Namensspalte zu, bevor Sie fortfahren.', preview: 'Vorschau',
            attHeading: 'Teilnahmewerte', attHint: 'Diese Werte kennen wir nicht — was bedeuten sie?',
            fLastName: 'Nachname', fLastNameHint: 'Steht der Nachname in einer eigenen Spalte, wird er mit einem Leerzeichen an den Namen angehängt.', fAttending: 'Teilnahme', fGuests: 'Anzahl Gäste', fAdults: 'Erwachsene', fChildren: 'Kinder',
            fDietary: 'Essen / Ernährung', fRelationship: 'Beziehung', fEmail: 'E-Mail', fPhone: 'Telefon', fDate: 'Antwortdatum', fCustom: 'Zusätzliche Felder',
            fCompany: 'Unternehmen', fJobTitle: 'Position', fDepartment: 'Abteilung',
            fTable: 'Tisch', fTableHint: 'Setzt diese Gäste an den genannten Tisch. Ein noch nicht vorhandener Tisch wird angelegt.',
            pHeading: 'Vor dem Import prüfen', pPhonesFixed: (n) => `${n} Telefonnummer${n === 1 ? '' : 'n'} ins internationale Format gebracht`, pPhonesKept: (n) => `${n} Telefonnummer${n === 1 ? '' : 'n'} nicht lesbar, wird unverändert übernommen`, pImport: (n) => `${n} werden importiert`, pUpdate: (n) => `${n} aktualisieren vorhandene`, pSkip: (n) => `${n} Duplikat${n === 1 ? '' : 'e'} übersprungen`, pInvalid: (n) => `${n} übersprungen (kein Name)`,
            mode: 'Duplikate', modeSkip: 'Duplikate überspringen', modeUpdate: 'Vorhandene aktualisieren', modeAll: 'Alles importieren', running: 'Datei wird geprüft…',
            toolsTitle: 'Organisieren Sie alle Gäste', toolsBody: 'Sitzplan, Check-in und vollständige Statistiken sind in den kostenpflichtigen Plänen — upgraden Sie, um Ihre ganze Liste zu organisieren.', upgrade: 'Pläne ansehen',
            rHeading: 'Import abgeschlossen', rImported: (n) => `${n} importiert`, rUpdated: (n) => `${n} aktualisiert`, rSkipped: (n) => `${n} übersprungen`, rInvalid: (n) => `${n} ungültig`,
            rSeated: (n, c) => `${n} platziert${c ? ` (${c} neue${c === 1 ? 'r' : ''} Tisch${c === 1 ? '' : 'e'})` : ''}`,
            rSeatFailed: (n) => `${n} konnten nicht platziert werden`,
            rSeatOver: (name, n, cap) => `${name} hat jetzt ${n} Gäste, bietet aber ${cap} Plätze`,
            undo: 'Diesen Import rückgängig machen', undoing: 'Wird rückgängig gemacht…', undone: 'Import rückgängig gemacht.', undoFail: 'Import konnte nicht rückgängig gemacht werden.',
            prevHeading: 'Frühere Importe', prevRemove: 'Entfernen', prevRemoving: 'Wird entfernt…',
            prevGuests: (n) => `${n} importierte${n === 1 ? 'r' : ''} ${n === 1 ? 'Gast' : 'Gäste'}`, prevKept: (n) => n === 1 ? '1 hat bereits geantwortet — bleibt erhalten' : `${n} haben bereits geantwortet — bleiben erhalten`,
            prevConfirm: (n) => `${n} importierte Gäste aus diesem Event entfernen? Gäste, die bereits geantwortet haben, bleiben erhalten. Das kann nicht rückgängig gemacht werden.`,
            prevRemoved: 'Importierte Gäste entfernt.', prevFail: 'Dieser Import konnte nicht entfernt werden.',
            failed: 'Import fehlgeschlagen', partial: 'Einige Zeilen wurden vor einem Fehler gespeichert. Sie können sie unten rückgängig machen.',
            noFormSelected: 'Bitte wählen Sie zuerst ein Event', yes: 'Ja', no: 'Nein', maybe: 'Vielleicht', pending: 'Antwort ausstehend', skipVal: 'Überspringen'
        },
        pt: {
            title: 'Importar convidados', sUpload: 'Enviar', sMap: 'Mapear', sPreview: 'Pré-visualizar', sDone: 'Concluído',
            back: 'Voltar', next: 'Avançar', cancel: 'Cancelar', importBtn: 'Importar', importing: 'Importando…', close: 'Fechar', viewResponses: 'Ver respostas',
            uSub: 'Envie um arquivo CSV com os convidados que já responderam ou com a sua lista de convidados inicial.',
            intentRsvps: 'Respostas já recebidas', intentRsvpsSub: 'Envie os convidados que já lhe deram a resposta (um convidado sem resposta no arquivo é importado como aguardando resposta).',
            intentGuestlist: 'Lista de convidados (ainda sem respostas)', intentGuestlistSub: 'Envie a sua lista de convidados. Eles aparecerão como aguardando resposta, com o número de pessoas atribuído.',
            ioLabel: 'RSVP apenas por convite', ioDesc: 'Os convidados da sua lista (ou com um link pessoal) respondem até o número de pessoas atribuído; qualquer outra pessoa pode enviar a você um pedido para participar, que você aprova ou recusa.',
            ioOn: 'RSVP apenas por convite ativado', ioOff: 'RSVP apenas por convite desativado', ioFail: 'Não foi possível alterar o RSVP apenas por convite. Tente novamente.',
            fGuestsAllowance: 'Pessoas por convite',
            browse: 'escolha um arquivo', template: 'Baixar modelo', notMessaged: 'Elas viram respostas reais. Seus convidados não receberão mensagem nem e-mail.', glNote: 'Estes convidados são adicionados como aguardando resposta. Nada é enviado agora: convide-os por mensagem ou e-mail em Mensagens quando quiser.', uSub2: 'Que tipo de lista você está enviando?',
            noHeader: 'Meu arquivo não tem linha de cabeçalho',
            detected: (n) => `${n} linha${n === 1 ? '' : 's'} detectada${n === 1 ? '' : 's'}.`, empty: 'Esse arquivo não tem linhas legíveis.', parseErr: 'Não foi possível ler o arquivo. Exporte como CSV e tente novamente.',
            mHeading: 'Combine suas colunas', mHint: 'Combinamos suas colunas automaticamente — ajuste o que precisar.',
            required: 'obrigatório', ignore: '— Ignorar —', nameReq: 'Mapeie a coluna Nome antes de continuar.', preview: 'Pré-visualização',
            attHeading: 'Valores de presença', attHint: 'Não reconhecemos estes valores — diga o que significam:',
            fLastName: 'Sobrenome', fLastNameHint: 'Se o sobrenome estiver em uma coluna própria, ele é adicionado depois do nome, com um espaço.', fAttending: 'Presença', fGuests: 'Número de convidados', fAdults: 'Adultos', fChildren: 'Crianças',
            fDietary: 'Refeição / dieta', fRelationship: 'Relação', fEmail: 'E-mail', fPhone: 'Telefone', fDate: 'Data da resposta', fCustom: 'Campos personalizados',
            fCompany: 'Empresa', fJobTitle: 'Cargo', fDepartment: 'Departamento',
            fTable: 'Mesa', fTableHint: 'Senta estes convidados na mesa indicada. Uma mesa que não exista é criada.',
            pHeading: 'Revise antes de importar', pPhonesFixed: (n) => `${n} telefone${n === 1 ? '' : 's'} em formato internacional`, pPhonesKept: (n) => `${n} telefone${n === 1 ? '' : 's'} não pôde ser lido e será mantido como está`, pImport: (n) => `${n} serão importadas`, pUpdate: (n) => `${n} atualizarão existentes`, pSkip: (n) => `${n} duplicada${n === 1 ? '' : 's'} ignorada${n === 1 ? '' : 's'}`, pInvalid: (n) => `${n} ignorada${n === 1 ? '' : 's'} (sem nome)`,
            mode: 'Duplicadas', modeSkip: 'Ignorar duplicadas', modeUpdate: 'Atualizar existentes', modeAll: 'Importar tudo', running: 'Verificando seu arquivo…',
            toolsTitle: 'Organize todos os convidados', toolsBody: 'Mapa de mesas, check-in e estatísticas completas estão nos planos pagos — faça upgrade para organizar toda a sua lista.', upgrade: 'Ver planos',
            rHeading: 'Importação concluída', rImported: (n) => `${n} importadas`, rUpdated: (n) => `${n} atualizadas`, rSkipped: (n) => `${n} ignoradas`, rInvalid: (n) => `${n} inválidas`,
            rSeated: (n, c) => `${n} sentados${c ? ` (${c} mesa${c === 1 ? '' : 's'} nova${c === 1 ? '' : 's'})` : ''}`,
            rSeatFailed: (n) => `${n} não puderam ser sentados`,
            rSeatOver: (name, n, cap) => `${name} tem agora ${n} convidados mas é para ${cap}`,
            undo: 'Desfazer esta importação', undoing: 'Desfazendo…', undone: 'Importação desfeita.', undoFail: 'Não foi possível desfazer a importação.',
            prevHeading: 'Importações anteriores', prevRemove: 'Remover', prevRemoving: 'Removendo…',
            prevGuests: (n) => `${n} convidado${n === 1 ? '' : 's'} importado${n === 1 ? '' : 's'}`, prevKept: (n) => `${n} já responde${n === 1 ? 'u' : 'ram'} — mantido${n === 1 ? '' : 's'}`,
            prevConfirm: (n) => `Remover ${n} convidado${n === 1 ? '' : 's'} importado${n === 1 ? '' : 's'} deste evento? Quem já respondeu é mantido. Isso não pode ser desfeito.`,
            prevRemoved: 'Convidados importados removidos.', prevFail: 'Não foi possível remover essa importação.',
            failed: 'A importação falhou', partial: 'Algumas linhas foram salvas antes de um erro. Você pode desfazê-las abaixo.',
            noFormSelected: 'Selecione um evento primeiro', yes: 'Sim', no: 'Não', maybe: 'Talvez', pending: 'Sem resposta', skipVal: 'Ignorar'
        },
        he: {
            title: 'ייבוא אורחים', sUpload: 'העלאה', sMap: 'מיפוי', sPreview: 'תצוגה מקדימה', sDone: 'סיום',
            back: 'חזרה', next: 'הבא', cancel: 'ביטול', importBtn: 'ייבוא', importing: 'מייבא…', close: 'סגירה', viewResponses: 'צפייה בתגובות',
            uSub: 'העלו קובץ CSV עם אורחים שכבר אישרו הגעה או עם רשימת המוזמנים הראשונית שלכם.',
            intentRsvps: 'אישורי הגעה שכבר התקבלו', intentRsvpsSub: 'העלו אורחים שכבר נתנו לכם תשובה (אורח ללא תשובה בקובץ מיובא כממתין לתשובה).',
            intentGuestlist: 'רשימת מוזמנים (עוד בלי תשובות)', intentGuestlistSub: 'העלו את רשימת המוזמנים. האורחים יופיעו כממתינים לתשובה, עם מספר האנשים שהוקצה להם.',
            ioLabel: 'אישור הגעה בהזמנה בלבד', ioDesc: 'אורחים מהרשימה שלכם (או בעלי קישור אישי) מאשרים הגעה עד מספר האנשים שהוקצה להם; כל אחד אחר יכול לשלוח לכם בקשת הצטרפות, שאותה תאשרו או תדחו.',
            ioOn: 'אישור הגעה בהזמנה בלבד הופעל', ioOff: 'אישור הגעה בהזמנה בלבד כובה', ioFail: 'לא הצלחנו לשנות את אישור ההגעה בהזמנה בלבד. נסו שוב.',
            fGuestsAllowance: 'אנשים לכל הזמנה',
            browse: 'בחרו קובץ', template: 'הורדת תבנית', notMessaged: 'אלה הופכים לתגובות אמיתיות. לא יישלחו לאורחים הודעות או מיילים.', glNote: 'האורחים האלה נוספים כממתינים לתשובה. שום דבר לא נשלח עכשיו: הזמינו אותם בהודעה או במייל מתוך ״הודעות״ כשתהיו מוכנים.', uSub2: 'איזו רשימה אתם מעלים?',
            noHeader: 'לקובץ שלי אין שורת כותרת',
            detected: (n) => `זוהו ${n} שורות.`, empty: 'בקובץ הזה אין שורות שניתן לקרוא.', parseErr: 'לא ניתן לקרוא את הקובץ. ייצאו אותו כ-CSV ונסו שוב.',
            mHeading: 'התאימו את העמודות', mHint: 'התאמנו את העמודות אוטומטית — עדכנו כל דבר שנראה לא נכון.',
            required: 'חובה', ignore: '— התעלם —', nameReq: 'יש למפות את עמודת השם לפני שממשיכים.', preview: 'תצוגה מקדימה',
            attHeading: 'ערכי השתתפות', attHint: 'לא זיהינו את הערכים האלה — ספרו לנו מה המשמעות:',
            fLastName: 'שם משפחה', fLastNameHint: 'אם שם המשפחה נמצא בעמודה נפרדת, הוא מתווסף אחרי השם עם רווח.', fAttending: 'השתתפות', fGuests: 'מספר אורחים', fAdults: 'מבוגרים', fChildren: 'ילדים',
            fDietary: 'ארוחה / תזונה', fRelationship: 'קשר', fEmail: 'אימייל', fPhone: 'טלפון', fDate: 'תאריך תגובה', fCustom: 'שדות מותאמים',
            fCompany: 'חברה', fJobTitle: 'תפקיד', fDepartment: 'מחלקה',
            fTable: 'שולחן', fTableHint: 'מושיב את האורחים האלה בשולחן שתציין. שולחן שאינו קיים ייווצר.',
            pHeading: 'בדקו לפני הייבוא', pPhonesFixed: (n) => `${n} מספרי טלפון הומרו לפורמט בינלאומי`, pPhonesKept: (n) => `${n} מספרי טלפון לא זוהו ויישמרו בדיוק כפי שנכתבו`, pImport: (n) => `${n} ייובאו`, pUpdate: (n) => `${n} יעדכנו קיימות`, pSkip: (n) => `${n} כפילויות דולגו`, pInvalid: (n) => `${n} דולגו (ללא שם)`,
            mode: 'כפילויות', modeSkip: 'דלג על כפילויות', modeUpdate: 'עדכן קיימות', modeAll: 'ייבא הכול', running: 'בודק את הקובץ…',
            toolsTitle: 'ארגנו את כל האורחים', toolsBody: 'סידורי הושבה, צ\'ק-אין וסטטיסטיקות מלאות זמינים בתוכניות בתשלום — שדרגו כדי לארגן את כל רשימת האורחים.', upgrade: 'הצג תוכניות',
            rHeading: 'הייבוא הושלם', rImported: (n) => `${n} יובאו`, rUpdated: (n) => `${n} עודכנו`, rSkipped: (n) => `${n} דולגו`, rInvalid: (n) => `${n} לא תקינות`,
            rSeated: (n, c) => `${n} הושבו${c ? ` (${c} שולחנות חדשים)` : ''}`,
            rSeatFailed: (n) => `${n} לא ניתן היה להושיב`,
            rSeatOver: (name, n, cap) => `ב${name} יש כעת ${n} אורחים אך יש בו ${cap} מקומות`,
            undo: 'בטל את הייבוא', undoing: 'מבטל…', undone: 'הייבוא בוטל.', undoFail: 'לא ניתן לבטל את הייבוא.',
            prevHeading: 'ייבואים קודמים', prevRemove: 'הסרה', prevRemoving: 'מסיר…',
            prevGuests: (n) => `${n} ${n === 1 ? 'אורח שיובא' : 'אורחים שיובאו'}`, prevKept: (n) => `${n} ${n === 1 ? 'כבר השיב — יישאר' : 'כבר השיבו — יישארו'}`,
            prevConfirm: (n) => `להסיר ${n} אורחים שיובאו מהאירוע הזה? אורחים שכבר השיבו יישארו. לא ניתן לבטל את הפעולה.`,
            prevRemoved: 'האורחים שיובאו הוסרו.', prevFail: 'לא ניתן להסיר את הייבוא.',
            failed: 'הייבוא נכשל', partial: 'חלק מהשורות נשמרו לפני שאירעה שגיאה. אפשר לבטל אותן למטה.',
            noFormSelected: 'בחרו אירוע תחילה', yes: 'כן', no: 'לא', maybe: 'אולי', pending: 'ממתין לתשובה', skipVal: 'דלג'
        },
        ar: {
            title: 'استيراد الضيوف', sUpload: 'رفع', sMap: 'مطابقة', sPreview: 'معاينة', sDone: 'تم',
            back: 'رجوع', next: 'التالي', cancel: 'إلغاء', importBtn: 'استيراد', importing: 'جارٍ الاستيراد…', close: 'إغلاق', viewResponses: 'عرض الردود',
            uSub: 'ارفع ملف CSV يضم الضيوف الذين أكدوا حضورهم بالفعل أو قائمة ضيوفك الأولية.',
            intentRsvps: 'ردود تم جمعها بالفعل', intentRsvpsSub: 'ارفع الضيوف الذين أعطوك إجاباتهم بالفعل (يُستورد الضيف الذي لا توجد له إجابة في الملف كبانتظار الرد).',
            intentGuestlist: 'قائمة الضيوف (بلا ردود بعد)', intentGuestlistSub: 'ارفع قائمة المدعوين. سيظهر الضيوف بحالة «بانتظار الرد» مع عدد الأشخاص المخصص لكل منهم.',
            ioLabel: 'تأكيد الحضور بالدعوة فقط', ioDesc: 'يؤكد الضيوف في قائمتك (أو من لديهم رابط شخصي) حضورهم ضمن عدد الأشخاص المخصص لهم، ويمكن لأي شخص آخر أن يرسل إليك طلب انضمام توافق عليه أو ترفضه.',
            ioOn: 'تم تفعيل تأكيد الحضور بالدعوة فقط', ioOff: 'تم إيقاف تأكيد الحضور بالدعوة فقط', ioFail: 'تعذّر تغيير تأكيد الحضور بالدعوة فقط. حاول مرة أخرى.',
            fGuestsAllowance: 'عدد الأشخاص لكل دعوة',
            browse: 'اختر ملفًا', template: 'تنزيل القالب', notMessaged: 'تصبح ردودًا حقيقية. لن تُرسل لضيوفك أي رسائل أو بريد.', glNote: 'يُضاف هؤلاء الضيوف بحالة «بانتظار الرد». لا يُرسل شيء الآن: ادعُهم برسالة أو بريد من «الرسائل» عندما تكون جاهزًا.', uSub2: 'ما نوع القائمة التي ترفعها؟',
            noHeader: 'ملفي لا يحتوي على صف عناوين',
            detected: (n) => `تم اكتشاف ${n} صفًا.`, empty: 'هذا الملف لا يحتوي على صفوف قابلة للقراءة.', parseErr: 'تعذّرت قراءة الملف. صدّره كـ CSV وحاول مجددًا.',
            mHeading: 'طابق أعمدتك', mHint: 'طابقنا أعمدتك تلقائيًا — عدّل ما يبدو غير صحيح.',
            required: 'مطلوب', ignore: '— تجاهل —', nameReq: 'يرجى مطابقة عمود الاسم قبل المتابعة.', preview: 'معاينة',
            attHeading: 'قيم الحضور', attHint: 'لم نتعرف على هذه القيم — أخبرنا بمعناها:',
            fLastName: 'اسم العائلة', fLastNameHint: 'إذا كان اسم العائلة في عمود منفصل، فإنه يُضاف بعد الاسم مع مسافة.', fAttending: 'الحضور', fGuests: 'عدد الضيوف', fAdults: 'البالغون', fChildren: 'الأطفال',
            fDietary: 'الوجبة / النظام الغذائي', fRelationship: 'العلاقة', fEmail: 'البريد', fPhone: 'الهاتف', fDate: 'تاريخ الرد', fCustom: 'حقول مخصصة',
            fCompany: 'الشركة', fJobTitle: 'المسمى الوظيفي', fDepartment: 'القسم',
            fTable: 'الطاولة', fTableHint: 'يجلس هؤلاء الضيوف على الطاولة التي تحددها. تُنشأ الطاولة إن لم تكن موجودة.',
            pHeading: 'راجع قبل الاستيراد', pPhonesFixed: (n) => `${n} رقم هاتف حُوّل إلى الصيغة الدولية`, pPhonesKept: (n) => `${n} رقم هاتف تعذّرت قراءته وسيُحفظ كما كُتب`, pImport: (n) => `${n} ستُستورد`, pUpdate: (n) => `${n} ستحدّث الموجود`, pSkip: (n) => `${n} مكرر تم تخطيه`, pInvalid: (n) => `${n} تم تخطيه (بدون اسم)`,
            mode: 'المكررات', modeSkip: 'تخطي المكررات', modeUpdate: 'تحديث الموجود', modeAll: 'استيراد الكل', running: 'جارٍ فحص ملفك…',
            toolsTitle: 'نظّم كل ضيوفك', toolsBody: 'توزيع المقاعد وتسجيل الوصول والإحصائيات الكاملة في الخطط المدفوعة — قم بالترقية لتنظيم قائمتك بالكامل.', upgrade: 'عرض الخطط',
            rHeading: 'اكتمل الاستيراد', rImported: (n) => `${n} مستوردة`, rUpdated: (n) => `${n} محدّثة`, rSkipped: (n) => `${n} متخطاة`, rInvalid: (n) => `${n} غير صالحة`,
            rSeated: (n, c) => `تم إجلاس ${n}${c ? ` (${c} طاولة جديدة)` : ''}`,
            rSeatFailed: (n) => `تعذّر إجلاس ${n}`,
            rSeatOver: (name, n, cap) => `${name} بها الآن ${n} ضيوف لكنها تتسع لـ ${cap}`,
            undo: 'التراجع عن هذا الاستيراد', undoing: 'جارٍ التراجع…', undone: 'تم التراجع عن الاستيراد.', undoFail: 'تعذّر التراجع عن الاستيراد.',
            prevHeading: 'عمليات الاستيراد السابقة', prevRemove: 'إزالة', prevRemoving: 'جارٍ الإزالة…',
            prevGuests: (n) => n === 1 ? 'ضيف مستورد واحد' : `${n} ضيوف مستوردين`, prevKept: (n) => n === 1 ? 'واحد ردّ بالفعل — سيبقى' : `${n} ردّوا بالفعل — سيبقون`,
            prevConfirm: (n) => `إزالة ${n} ضيفًا مستوردًا من هذا الحدث؟ سيبقى من ردّوا بالفعل. لا يمكن التراجع عن هذا.`,
            prevRemoved: 'تمت إزالة الضيوف المستوردين.', prevFail: 'تعذّرت إزالة عملية الاستيراد.',
            failed: 'فشل الاستيراد', partial: 'تم حفظ بعض الصفوف قبل حدوث خطأ. يمكنك التراجع عنها أدناه.',
            noFormSelected: 'يرجى تحديد حدث أولاً', yes: 'نعم', no: 'لا', maybe: 'ربما', pending: 'بانتظار الرد', skipVal: 'تخطى'
        }
    };
    return Object.assign({}, strings[lang] || strings.en, IMPORT_EXTRA_STRINGS[lang] || IMPORT_EXTRA_STRINGS.en);
}

let _importState = null;
function _resetImportState() {
    // intent: 'rsvps' = already-collected answers (historical default);
    //         'guestlist' = invitees who haven't replied — imported as
    //         attending='pending' ("Awaiting reply"), with each row's guest
    //         count doubling as the invite-only per-invitation allowance.
    _importState = { fileName: '', parsed: null, hasHeader: true, mapping: null, mode: 'skip-dupes', busy: false, committedBatchIds: [], intent: 'rsvps', manual: false };
}

function _importEsc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/** Create the Import Responses modal shell (once). */
function ensureImportModal() {
    if (document.getElementById('importResponsesModal')) return;
    const t = getImportStrings();
    const pill = (n, label) => `<span class="import-pill" data-pill="${n}"><span class="import-pill-num">${n}</span><span class="import-pill-label">${label}</span></span>`;
    const modalHtml = `
        <div class="modal fade" id="importResponsesModal" tabindex="-1" aria-labelledby="importResponsesLabel" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="importResponsesLabel"><i class="bi bi-upload me-2"></i>${t.title}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="import-stepper d-flex gap-2 px-3 pt-3 flex-wrap" id="importStepper">
                        ${pill(1, t.sUpload)}${pill(2, t.sMap)}${pill(3, t.sPreview)}${pill(4, t.sDone)}
                    </div>
                    <div class="modal-body" id="importModalBody">
                        <div class="import-step" data-step="1"></div>
                        <div class="import-step" data-step="2" style="display:none;"></div>
                        <div class="import-step" data-step="3" style="display:none;"></div>
                        <div class="import-step" data-step="4" style="display:none;"></div>
                    </div>
                    <!-- No Cancel: the X closes the dialog (task #918). Buttons are
                         shown and hidden with [hidden] - see the style block below. -->
                    <div class="modal-footer" id="importFooter">
                        <button type="button" class="btn btn-outline-secondary me-auto" id="importBackBtn" hidden>${t.back}</button>
                        <button type="button" class="btn btn-primary" id="importNextBtn" hidden>${t.next}</button>
                        <button type="button" class="btn btn-primary" id="importCommitBtn" hidden><i class="bi bi-upload me-1"></i>${t.importBtn}</button>
                    </div>
                </div>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHtml);
    // Minimal styles (scoped) for the stepper — injected once.
    if (!document.getElementById('import-wizard-styles')) {
        const css = document.createElement('style');
        css.id = 'import-wizard-styles';
        css.textContent = `
            /* Task #784, round 2: admin.css stacks EVERY .d-flex.gap-2 into a column
               below 768px, which put Upload / Map / Preview / Done one per line on a
               phone, the header-row checkbox above Download template, and the notes'
               icons above their text. Rows here stay rows (they wrap if they must). */
            #importResponsesModal .d-flex.gap-2:not(.flex-column){flex-direction:row}
            /* Task #918: admin.css forces every .modal-footer .btn to
               display:inline-flex !important, which overrode style.display and
               showed Back, Next AND Import on step 1 - and Import pressed there
               spun on "Importing..." for good. The id outranks it. */
            #importResponsesModal .modal-footer .btn[hidden],#importResponsesModal .modal-footer[hidden],#importResponsesModal .import-stepper[hidden]{display:none!important}
            #importResponsesModal .import-pill{display:inline-flex;align-items:center;gap:.4rem;font-size:.8rem;color:#6c757d;opacity:.6}
            #importResponsesModal .import-pill.active{opacity:1;color:var(--bs-primary,#0d6efd);font-weight:600}
            #importResponsesModal .import-pill.done{opacity:1;color:#198754}
            #importResponsesModal .import-pill-num{display:inline-flex;width:1.5rem;height:1.5rem;border-radius:50%;background:#e9ecef;align-items:center;justify-content:center;font-size:.75rem}
            #importResponsesModal .import-pill.active .import-pill-num{background:var(--bs-primary,#0d6efd);color:#fff}
            #importResponsesModal .import-pill.done .import-pill-num{background:#198754;color:#fff}
            #importResponsesModal .import-intents{display:grid;gap:.5rem}
            #importResponsesModal .import-intent{border:1px solid #dee2e6;border-radius:.6rem;padding:.75rem .9rem .75rem 2.4rem;margin:0}
            #importResponsesModal .import-intent:has(> .form-check-input:checked){border-color:var(--bs-primary,#0d6efd);background:rgba(13,110,253,.03)}
            #importResponsesModal .import-invite-only{padding-top:.6rem;border-top:1px dashed #dee2e6}
            #importResponsesModal .import-dropzone{border:2px dashed #ced4da;border-radius:.75rem;padding:2rem 1rem;text-align:center;cursor:pointer;transition:border-color .15s}
            #importResponsesModal .import-dropzone.drag-over{border-color:var(--bs-primary,#0d6efd);background:rgba(13,110,253,.04)}
            /* ---- Round 2 of #918: lighter, quieter, one clear action ---- */
            #importResponsesModal .modal-content{border:0;border-radius:14px;box-shadow:0 24px 60px -20px rgba(16,24,40,.35)}
            #importResponsesModal .modal-header{padding:1rem 1.25rem;border-bottom:1px solid #eef0f3}
            #importResponsesModal .modal-title{font-size:1.05rem;font-weight:600}
            #importResponsesModal .modal-title .bi{color:#6b7280}
            #importResponsesModal .modal-body{padding:1rem 1.25rem 1.25rem}
            #importResponsesModal .import-stepper{padding:.85rem 1.25rem 0 !important;gap:1rem !important}
            #importResponsesModal .import-pill-num{width:1.35rem;height:1.35rem;font-size:.7rem}
            /* The main action sits at the end of the row (Back keeps its place at the start). */
            #importResponsesModal #importFooter{justify-content:flex-end !important;gap:.5rem;padding:.75rem 1.25rem;border-top:1px solid #eef0f3}
            #importResponsesModal #importFooter .btn{min-height:2.25rem;padding:.4rem 1rem;border-radius:8px;font-size:.875rem}
            #importResponsesModal .import-intent{padding:.65rem .9rem .65rem 2.3rem;border-color:#e5e7eb;border-radius:10px;transition:border-color .15s,background-color .15s}
            #importResponsesModal .import-intent:hover{border-color:#cbd2da}
            #importResponsesModal .import-intent .fw-semibold{font-size:.9rem}
            #importResponsesModal .import-intent:has(> .form-check-input:checked){border-color:#1f2937;background:#f9fafb}
            #importResponsesModal .import-dropzone{border:1.5px dashed #d0d5dd;border-radius:12px;padding:1.6rem 1rem;background:#fafbfc;transition:border-color .15s,background-color .15s}
            #importResponsesModal .import-dropzone:hover{border-color:#98a2b3;background:#f5f6f8}
            #importResponsesModal #importNoteBox{background:#f9fafb;border-color:#eef0f3 !important;border-radius:10px;color:#4b5563}
            /* Typing invitees in: only the invite-only switch stays from the top. */
            #importResponsesModal.imp-manual-mode #importLead,
            #importResponsesModal.imp-manual-mode .import-intent:has(#importIntentRsvps),
            #importResponsesModal.imp-manual-mode #importIntentGuestlist,
            #importResponsesModal.imp-manual-mode label[for="importIntentGuestlist"],
            #importResponsesModal.imp-manual-mode #importNoteBox{display:none !important}
            #importResponsesModal.imp-manual-mode .import-intents{margin-bottom:1rem !important}
            #importResponsesModal.imp-manual-mode .import-intent{border:0;background:none !important;padding:0}
            #importResponsesModal.imp-manual-mode .import-invite-only{display:grid !important;grid-template-columns:auto 1fr;column-gap:.75rem;align-items:start;margin-top:0 !important;padding:.65rem .85rem;border:1px solid #eef0f3;border-radius:10px;background:#f9fafb}
            #importResponsesModal.imp-manual-mode .import-invite-only > .form-check-input{grid-row:span 2;float:none;margin:.15rem 0 0 !important}
            #importResponsesModal.imp-manual-mode .import-invite-only > label,#importResponsesModal.imp-manual-mode .import-invite-only > span{grid-column:2}
            /* The sheet */
            #importResponsesModal .im-sheet{border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;background:#fff}
            #importResponsesModal .im-sheet .table-responsive{margin:0}
            #importResponsesModal .im-table{width:100%;min-width:760px;border-collapse:collapse;table-layout:fixed}
            #importResponsesModal .im-c-name{width:22%}
            #importResponsesModal .im-c-group{width:18%}
            #importResponsesModal .im-c-phone{width:16%}
            #importResponsesModal .im-c-email{width:19%}
            #importResponsesModal .im-c-max{width:96px}
            #importResponsesModal .im-c-tags{width:13%}
            #importResponsesModal .im-c-x{width:34px}
            /* How the invitation reads (task #935): one compact card under the
               sheet / the mapping preview, wrapping on a phone, names isolated
               with <bdi> so a Hebrew dialog keeps Latin names in order. */
            #importResponsesModal .imp-gl-card{margin-top:.75rem;padding:.6rem .75rem;border:1px solid #e5e7eb;border-radius:10px;background:#fcfcfd;font-size:.82rem;color:#374151}
            #importResponsesModal .imp-gl-card-h{margin-bottom:.3rem;font-size:.7rem;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:#6b7280}
            #importResponsesModal .imp-gl-line{display:flex;align-items:baseline;gap:.45rem;min-width:0;margin-top:.2rem}
            #importResponsesModal .imp-gl-line > i{flex:0 0 auto;color:#9ca3af}
            #importResponsesModal .imp-gl-line > span{min-width:0;overflow-wrap:anywhere}
            #importResponsesModal .imp-gl-addr{font-weight:600;color:#111827}
            #importResponsesModal .imp-gl-finds{margin:.15rem 0 0;padding-inline-start:1.1rem;font-weight:500;color:#111827}
            #importResponsesModal .imp-gl-tip{display:flex;align-items:baseline;gap:.45rem;margin-top:.45rem;padding:.4rem .55rem;border-radius:8px;background:#fffbeb;color:#92400e}
            #importResponsesModal .imp-gl-notes:empty{display:none}
            #importResponsesModal .imp-gl-note{display:flex;align-items:baseline;gap:.45rem;margin-top:.35rem;font-size:.82rem;color:#4b5563;overflow-wrap:anywhere}
            #importResponsesModal .imp-gl-warn{margin-top:.5rem;padding:.45rem .6rem;border:1px solid #fed7aa;border-radius:8px;background:#fff7ed;color:#9a3412;font-size:.82rem;overflow-wrap:anywhere}
            #importResponsesModal .im-table th{padding:.5rem .6rem;background:#f9fafb;border-bottom:1px solid #e5e7eb;font-size:.7rem;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:#6b7280;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;text-align:start}
            #importResponsesModal .im-table td{padding:0;border-bottom:1px solid #f1f2f4;border-inline-end:1px solid #f1f2f4}
            #importResponsesModal .im-table td:last-child{border-inline-end:0}
            #importResponsesModal .im-table tbody tr:last-child td{border-bottom:0}
            #importResponsesModal .im-cell{display:block;width:100%;height:2.4rem;padding:0 .6rem;border:0;outline:0;background:transparent;font-size:.875rem;color:#111827;box-shadow:inset 0 0 0 1.5px transparent;transition:box-shadow .12s,background-color .12s}
            #importResponsesModal .im-cell::placeholder{color:#b6bcc6}
            /* Numbers and addresses read left to right in any language (the RTL pass sets text-align inline). */
            #importResponsesModal .im-cell[dir="ltr"]{direction:ltr !important;text-align:left !important}
            #importResponsesModal .im-cell:hover{background:#fafbfc}
            #importResponsesModal .im-cell:focus{background:#fff;box-shadow:inset 0 0 0 1.5px #1f2937}
            #importResponsesModal .im-max{-moz-appearance:textfield;appearance:textfield}
            #importResponsesModal .im-max::-webkit-outer-spin-button,#importResponsesModal .im-max::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
            /* The tag picker (bulk-sharing-wizard.js) swaps the input for a .qtp-box; in the sheet it is a cell too. */
            #importResponsesModal .im-table .qtp-box{height:2.4rem !important;min-height:2.4rem !important;margin:0 !important;padding-block:0 !important;padding-inline:.6rem 1.6rem !important;border:0 !important;border-radius:0 !important;box-shadow:none !important;background-color:transparent !important;flex-wrap:nowrap;overflow:hidden;font-size:.875rem}
            /* Its caret sits on the left of an RTL dialog whatever the text direction. */
            #importResponsesModal[dir="rtl"] .im-table .qtp-box{padding-left:1.6rem !important;padding-right:.6rem !important}
            #importResponsesModal .im-table .qtp-box:hover{background-color:#fafbfc !important}
            #importResponsesModal .im-table .qtp-box:focus{outline:0;box-shadow:inset 0 0 0 1.5px #1f2937 !important}
            #importResponsesModal .im-table .qtp-ph{color:#b6bcc6}
            #importResponsesModal .im-x{text-align:center}
            #importResponsesModal .im-remove{border:0;background:none;padding:.3rem;color:#9ca3af;line-height:1;border-radius:6px;opacity:0;transition:opacity .12s,color .12s}
            #importResponsesModal .im-table tr:hover .im-remove,#importResponsesModal .im-remove:focus-visible{opacity:1}
            #importResponsesModal .im-remove:hover{color:#dc3545;background:#fef2f2}
            @media (hover:none){#importResponsesModal .im-remove{opacity:1}}
            #importResponsesModal .im-add{display:flex;align-items:center;gap:.4rem;width:100%;padding:.55rem .75rem;border:0;border-top:1px solid #e5e7eb;background:#fff;color:#4b5563;font-size:.85rem;font-weight:500;text-align:start}
            #importResponsesModal .im-add:hover{background:#f9fafb;color:#111827}
            #importResponsesModal .im-back{font-size:.82rem;color:#6b7280;text-decoration:none}
            #importResponsesModal .im-back:hover{color:#111827}
        `;
        document.head.appendChild(css);
    }
}

function _importSetStep(n) {
    const modal = document.getElementById('importResponsesModal');
    if (!modal) return;
    modal.querySelectorAll('.import-step').forEach(el => {
        el.style.display = (Number(el.dataset.step) === n) ? '' : 'none';
    });
    modal.querySelectorAll('.import-pill').forEach(el => {
        const s = Number(el.dataset.pill);
        el.classList.toggle('active', s === n);
        el.classList.toggle('done', s < n);
    });
    const t = getImportStrings();
    const manual = !!_importState.manual;
    const back = document.getElementById('importBackBtn');
    const next = document.getElementById('importNextBtn');
    const commit = document.getElementById('importCommitBtn');
    // One primary action per step (task #918). Step 1 has none for a file -
    // it moves on by itself the moment it is read - and a typed-in guest list
    // is a single step: one "Add to guest list" that saves (task #881).
    back.hidden = !(n === 2 || n === 3);
    next.hidden = !(n === 2 || (n === 1 && manual));
    next.textContent = (n === 1 && manual) ? t.manualSave : t.next;
    commit.hidden = n !== 3;
    // Step 4 carries its own Close (and Undo) in the body.
    document.getElementById('importFooter').hidden = n === 4 || (n === 1 && !manual);
    // Typing guests in has no Upload / Map / Preview / Done to step through.
    document.getElementById('importStepper').hidden = n === 1 && manual;
    _importState.step = n;
}

function openImportResponsesModal(ctx, opts = {}) {
    // A click handler passes an Event; only a real context (with a formId) counts.
    _importCtx = (ctx && ctx.formId) ? ctx : null;
    const t = getImportStrings();
    if (!_impFormId()) { _impToast(t.noFormSelected, 'error'); return; }
    ensureImportModal();
    _resetImportState();
    _renderImportUpload();
    _importSetStep(1);
    // Wire footer nav (idempotent per open — reset onclicks)
    // Typed-in guests skip the column step: there are no columns to match.
    document.getElementById('importBackBtn').onclick = () => _importSetStep(_importState.step === 3 && !_importState.manual ? 2 : 1);
    document.getElementById('importNextBtn').onclick = () => (_importState.manual && _importState.step === 1 ? _importManualSave() : _importValidateMappingAndPreview());
    document.getElementById('importCommitBtn').onclick = () => _importCommit();
    const modalEl = document.getElementById('importResponsesModal');
    modalEl.setAttribute('dir', ['he', 'ar'].includes(window.DASHBOARD_LANGUAGE) ? 'rtl' : 'ltr');
    new bootstrap.Modal(modalEl).show();
    // "Add invitees" (task #881): a guest list, typed in.
    if (opts && opts.manual) {
        const gl = document.getElementById('importIntentGuestlist');
        if (gl) { gl.checked = true; gl.onchange && gl.onchange(); }
        _importShowManual(true);
    }
}

// ---- Typing invitees in (task #881) ----------------------------------------
// A forgotten guest or two late additions should not need a spreadsheet: the
// guest list can be typed into a small table here - only what matters before
// anyone replies - and goes through the very same import (awaiting reply,
// invite-only allowance, party, tags) as a file would.
function _importManualRowHtml() {
    const t = getImportStrings();
    const tagAttrs = window.TagPicker ? `data-tag-picker data-tag-form="${_importEsc(_impFormId() || '')}" readonly` : '';
    // A sheet, not a form (task #918 round 2): borderless cells that light up
    // on focus, sized by the column widths in the style block.
    // Task #935: the guest list's six columns - Guest name(s) | Group name |
    // Phone | Email | Max guests | Tags. The people go in one cell ("Sarah
    // Smith; Toby (child)"); Party members is not a column of its own any more.
    return `<tr class="imp-manual-row">
        <td><input type="text" class="im-cell im-name" placeholder="${_importEsc(t.fGuestNames)}" title="${_importEsc(t.fGuestNamesHint)}" autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" dir="auto"></td>
        <td><input type="text" class="im-cell im-group" placeholder="${_importEsc(t.fGroupName)}" title="${_importEsc(t.fGroupNameHint)}" autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" dir="auto"></td>
        <td><input type="tel" class="im-cell im-phone" placeholder="+1 555 123 4567" autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" dir="ltr"></td>
        <td><input type="email" class="im-cell im-email" placeholder="name@example.com" autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" dir="ltr"></td>
        <td><input autocomplete="guest-details" autocorrect="off" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" type="number" min="1" max="50" class="im-cell im-max" title="${_importEsc(t.fGuestsAllowance)}" inputmode="numeric"></td>
        <td><input type="text" class="im-cell im-tags" ${tagAttrs} placeholder="family, vip" title="${_importEsc(t.fTagsHint)}" autocomplete="off" dir="auto"></td>
        <td class="im-x"><button type="button" class="im-remove" aria-label="×"><i class="bi bi-x-lg"></i></button></td>
    </tr>`;
}
function _importShowManual(on) {
    const t = getImportStrings();
    _importState.manual = !!on;
    // Typing invitees in is a guest list by definition, so the two "what kind
    // of list" cards step aside and only the invite-only switch stays (#918 r2).
    document.getElementById('importResponsesModal')?.classList.toggle('imp-manual-mode', !!on);
    if (_importState.step === 1 || _importState.step === undefined) _importSetStep(1);
    const zone = document.getElementById('importDropzone');
    const fileRow = document.getElementById('importFileRow');
    let wrap = document.getElementById('importManualWrap');
    if (zone) zone.style.display = on ? 'none' : '';
    // d-none, not style.display: the row is .d-flex, which is !important.
    if (fileRow) fileRow.classList.toggle('d-none', !!on);
    const link = document.getElementById('importManualLink');
    if (link) link.style.display = on ? 'none' : '';
    if (!on) { if (wrap) wrap.style.display = 'none'; return; }
    if (!wrap) {
        wrap = document.createElement('div');
        wrap.id = 'importManualWrap';
        wrap.innerHTML = `
            <p class="small text-muted mb-2">${_importEsc(t.manualHint)}</p>
            <div class="im-sheet">
                <div class="table-responsive"><table class="im-table" id="importManualTable">
                    <colgroup><col class="im-c-name"><col class="im-c-group"><col class="im-c-phone"><col class="im-c-email"><col class="im-c-max"><col class="im-c-tags"><col class="im-c-x"></colgroup>
                    <thead><tr><th>${_importEsc(t.fGuestNames)} <i class="bi bi-info-circle small js-hint-tip" tabindex="0" role="button" style="cursor:help;opacity:0.7;" title="${_importEsc(t.fGuestNamesHint)}"></i></th><th>${_importEsc(t.fGroupName)} <i class="bi bi-info-circle small js-hint-tip" tabindex="0" role="button" style="cursor:help;opacity:0.7;" title="${_importEsc(t.fGroupNameHint)}"></i></th><th>${_importEsc(t.fPhone)}</th><th>${_importEsc(t.fEmail)}</th>
                    <th title="${_importEsc(t.fGuestsAllowance)}">${_importEsc(t.fMax)}</th><th title="${_importEsc(t.fTagsHint)}">${_importEsc(t.fTags)}</th><th></th></tr></thead>
                    <tbody>${_importManualRowHtml()}${_importManualRowHtml()}${_importManualRowHtml()}</tbody>
                </table></div>
                <button type="button" class="im-add" id="importManualAdd"><i class="bi bi-plus-lg"></i>${_importEsc(t.addRow)}</button>
            </div>
            <div id="importManualCard"></div>
            <div class="imp-gl-notes" id="importManualNotes"></div>
            <button type="button" class="btn btn-sm btn-link px-0 mt-2 im-back" id="importManualBack"><i class="bi bi-upload me-1"></i>${_importEsc(t.backToFile)}</button>`;
        const anchor = document.getElementById('importDropzone');
        anchor.insertAdjacentElement('afterend', wrap);
        wrap.querySelector('#importManualAdd').onclick = () => {
            wrap.querySelector('tbody').insertAdjacentHTML('beforeend', _importManualRowHtml());
            wrap.querySelector('tbody tr:last-child .im-name')?.focus();
        };
        wrap.querySelector('#importManualBack').onclick = () => _importShowManual(false);
        wrap.addEventListener('click', (e) => {
            const x = e.target.closest('.im-remove');
            if (!x) return;
            const tbody = wrap.querySelector('tbody');
            x.closest('tr').remove();
            if (!tbody.children.length) tbody.insertAdjacentHTML('beforeend', _importManualRowHtml());
            _importManualLive();
        });
        // The preview card follows the row being typed (task #935); the checks
        // under it read every row.
        wrap.addEventListener('focusin', (e) => { const tr = e.target.closest('tr.imp-manual-row'); if (tr) _importManualLive(tr); });
        wrap.addEventListener('input', (e) => _importManualLive(e.target.closest('tr.imp-manual-row')));
    }
    wrap.style.display = '';
    wrap.querySelector('tbody tr .im-name')?.focus();
    _importManualLive();
}
// The typed sheet as rows keyed by heading - the same shape a parsed file has.
const IMP_MANUAL_HEADERS = ['Guest name(s)', 'Group name', 'Phone', 'Email', 'Max guests', 'Tags'];
function _importManualRowValues(tr) {
    const v = (cls) => (tr.querySelector(cls)?.value || '').trim();
    return { 'Guest name(s)': v('.im-name'), 'Group name': v('.im-group'), 'Phone': v('.im-phone'), 'Email': v('.im-email'), 'Max guests': v('.im-max'), 'Tags': v('.im-tags') };
}
function _importManualRows() {
    return [...document.querySelectorAll('#importManualTable tbody tr')].map(_importManualRowValues)
        .filter((r) => r['Guest name(s)'] || r['Group name'] || r.Phone || r.Email);
}
// Re-draw the card for the row being typed (else the first one with a name)
// and the checks over the whole sheet.
function _importManualLive(tr) {
    if (tr) _importState.manualRow = tr;
    const card = document.getElementById('importManualCard');
    const notes = document.getElementById('importManualNotes');
    if (!card || !notes) return;
    let row = _importState.manualRow && _importState.manualRow.isConnected ? _importManualRowValues(_importState.manualRow) : null;
    const rows = _importManualRows();
    if (!row || !(row['Guest name(s)'] || row['Group name'])) row = rows.find((r) => r['Guest name(s)'] || r['Group name']) || null;
    card.innerHTML = row ? _impInvitationCardHtml(_impInvitation(row['Guest name(s)'], row['Group name'], '')) : '';
    notes.innerHTML = _impGuestListNotesHtml(rows.map((r) => {
        const inv = _impInvitation(r['Guest name(s)'], r['Group name'], '');
        return { cell: r['Guest name(s)'], contact: inv.contact, email: r.Email, phone: r.Phone };
    }), { tip: false });
}
// The typed rows become a parsed "file" with known headings, and go to the
// review step through the same mapping and payload code as an upload.
async function _importManualSave() {
    const t = getImportStrings();
    const rows = _importManualRows();
    // A row needs a guest name or a group name (task #935).
    if (!rows.some((r) => r['Guest name(s)'] || r['Group name'])) { _impToast(t.manualNeedOne, 'error'); return; }
    const separate = (_impFormData() || {}).separateAdultsChildren === true;
    _importState.fileName = 'typed-in';
    _importState.parsed = { headers: IMP_MANUAL_HEADERS.slice(), rows };
    _importState.mapping = {
        guestName: 'Guest name(s)', groupName: 'Group name', phoneNumber: 'Phone', email: 'Email',
        guestCount: separate ? null : 'Max guests', adultCount: separate ? 'Max guests' : null,
        partyMembers: null, tags: 'Tags', customFields: {}, unmapped: []
    };
    // One step (task #918): typed-in rows have nothing to map and little to
    // review, so the button saves. A clean save closes the dialog; anything
    // skipped or failed lands on the result screen, which says why.
    _importState._payload = null;
    // Re-render the (hidden) mapping step: buildImportPayload reads its
    // custom-field selects, and an earlier file's would otherwise leak in.
    _renderImportMapping();
    const res = await _importCommit({ quiet: true });
    if (!res) return;
    const s = res.summary || {};
    if (res.ok && !s.skippedDuplicate && !s.invalid) {
        const modalEl = document.getElementById('importResponsesModal');
        bootstrap.Modal.getInstance(modalEl)?.hide();
        _impToast(t.manualAdded((s.imported || 0) + (s.updated || 0)), 'success');
    } else {
        _renderImportResult(res);
        _importSetStep(4);
    }
}

function _renderImportUpload() {
    const t = getImportStrings();
    const step = document.querySelector('#importResponsesModal .import-step[data-step="1"]');
    step.innerHTML = `
        <!-- Task #784: the title says what this is, so the body opens with one
             short line instead of a heading plus a run-on paragraph. -->
        <!-- Task #805: the line explains BOTH options; the note at the foot
             follows whichever one is chosen (see syncIntent). -->
        <p class="text-muted small mb-3" id="importLead">${t.uSub2 || t.uSub}</p>
        <div id="importPrevious"></div>
        <div class="import-intents mb-4">
            <div class="form-check import-intent">
                <input class="form-check-input" type="radio" name="importIntent" id="importIntentRsvps" value="rsvps" ${_importState.intent !== 'guestlist' ? 'checked' : ''}>
                <label class="form-check-label" for="importIntentRsvps"><span class="fw-semibold">${t.intentRsvps}</span><span class="d-block small text-muted">${t.intentRsvpsSub}</span></label>
            </div>
            <div class="form-check import-intent">
                <input class="form-check-input" type="radio" name="importIntent" id="importIntentGuestlist" value="guestlist" ${_importState.intent === 'guestlist' ? 'checked' : ''}>
                <label class="form-check-label" for="importIntentGuestlist"><span class="fw-semibold">${t.intentGuestlist}</span><span class="d-block small text-muted">${t.intentGuestlistSub}</span></label>
                <!-- Invite-only, right where a guest list is imported (task #784),
                     so the host can lock the form to it without going back to
                     the builder. The same Pro gate the builder's switch uses. -->
                <div class="form-check form-switch import-invite-only mt-2" id="importInviteOnlyWrap" style="display:none;">
                    <input class="form-check-input" type="checkbox" role="switch" id="importInviteOnlyToggle">
                    <label class="form-check-label small fw-semibold" for="importInviteOnlyToggle">${t.ioLabel}</label>
                    <span class="d-block small text-muted">${t.ioDesc}</span>
                </div>
            </div>
        </div>
        <div class="import-dropzone" id="importDropzone">
            <i class="bi bi-filetype-csv" style="font-size:2rem;color:#adb5bd;"></i>
            <div class="mt-2">${t.drop} <a href="#" id="importBrowseLink">${t.browse}</a></div>
            <div class="small text-muted mt-1">${t.csvOnly}</div>
            <input type="file" id="importFileInput" accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" style="display:none;">
        </div>
        <div class="text-center mt-2"><button type="button" class="btn btn-link btn-sm" id="importManualLink" style="display:none;"><i class="bi bi-keyboard me-1"></i>${t.typeIn}</button></div>
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3" id="importFileRow">
            <div class="form-check mb-0">
                <input class="form-check-input" type="checkbox" id="importNoHeader">
                <label class="form-check-label small" for="importNoHeader">${t.noHeader}</label>
            </div>
            <button type="button" class="btn btn-sm btn-outline-secondary" id="importTemplateBtn"><i class="bi bi-download me-1"></i>${t.template}</button>
        </div>
        <div class="small text-muted mt-2" id="importDetected"></div>
        <div class="alert alert-light border mt-3 mb-0 small d-flex align-items-start gap-2" id="importNoteBox">
            <i class="bi bi-shield-check text-success mt-1" id="importNoteIcon"></i><div id="importNote">${t.notMessaged}</div>
        </div>
    `;
    const input = document.getElementById('importFileInput');
    const zone = document.getElementById('importDropzone');
    document.getElementById('importBrowseLink').onclick = (e) => { e.preventDefault(); input.click(); };
    zone.onclick = (e) => { if (e.target.tagName !== 'A' && e.target.id !== 'importNoHeader') input.click(); };
    input.onchange = () => { if (input.files && input.files[0]) _importHandleFile(input.files[0]); };
    document.getElementById('importNoHeader').onchange = (e) => {
        _importState.hasHeader = !e.target.checked;
        if (_importState._lastFile) _importHandleFile(_importState._lastFile);
    };
    document.getElementById('importTemplateBtn').onclick = () => _downloadResponsesTemplate();
    document.getElementById('importManualLink').onclick = () => _importShowManual(true);
    if (window.FileHelpers?.setupDragAndDrop) {
        window.FileHelpers.setupDragAndDrop(zone, (file) => _importHandleFile(file));
    }
    const ioWrap = document.getElementById('importInviteOnlyWrap');
    const ioToggle = document.getElementById('importInviteOnlyToggle');
    if (ioToggle) {
        ioToggle.checked = (_impFormData() || {}).inviteOnly === true;
        ioToggle.onchange = () => _importSetInviteOnly(ioToggle);
    }
    const syncIntent = () => {
        _importState.intent = document.getElementById('importIntentGuestlist')?.checked ? 'guestlist' : 'rsvps';
        const ioOffered = !(_importCtx && _importCtx.inviteOnly === false);
        // Typing guests in is a guest-list thing (task #881): answers come from a file.
        const manualLink = document.getElementById('importManualLink');
        if (manualLink) manualLink.style.display = (_importState.intent === 'guestlist' && !_importState.manual) ? '' : 'none';
        if (_importState.intent !== 'guestlist' && _importState.manual) _importShowManual(false);
        if (ioWrap) ioWrap.style.display = (ioOffered && _importState.intent === 'guestlist') ? '' : 'none';
        // Task #805: "These become real responses" is only true of collected
        // RSVPs; a guest list is invitees waiting to be invited.
        const gl = _importState.intent === 'guestlist';
        const note = document.getElementById('importNote');
        if (note) note.textContent = gl ? (t.glNote || t.notMessaged) : t.notMessaged;
        const icon = document.getElementById('importNoteIcon');
        if (icon) icon.className = gl ? 'bi bi-send text-secondary mt-1' : 'bi bi-shield-check text-success mt-1';
        // The mapping step shows different fields per kind of list - match the
        // headings again for this kind, then rebuild it (#883).
        if (_importState.parsed) {
            _importState.mapping = _impAutoMap(_importState.parsed.headers);
            _renderImportMapping();
        }
    };
    document.getElementById('importIntentRsvps').onchange = syncIntent;
    document.getElementById('importIntentGuestlist').onchange = syncIntent;
    syncIntent();
    _renderImportPrevious();
}

/**
 * The Invite-only switch inside the import modal (task #784). Turning it ON
 * goes through the same Pro check as the builder's switch; turning it OFF is
 * never gated. Written straight to the form, the same field the builder saves.
 */
async function _importSetInviteOnly(toggle) {
    const t = getImportStrings();
    const want = !!toggle.checked;
    toggle.disabled = true;
    try {
        if (want && window.AccessControl?.checkFeatureAccess) {
            const ok = await window.AccessControl.checkFeatureAccess(_impFormId(), 'inviteOnly');
            if (!ok) {
                toggle.checked = false;
                if (window.AccessControl.promptForUpgrade) {
                    await window.AccessControl.promptForUpgrade(_impFormId(), 'inviteOnly', 'invite-only');
                }
                return;
            }
        }
        await firebase.firestore().collection('rsvp_forms').doc(_impFormId()).update({ inviteOnly: want });
        if (_impFormData()) _impFormData().inviteOnly = want;
        if (window.currentFormData && window.currentFormData !== _impFormData()) window.currentFormData.inviteOnly = want;
        _impToast(want ? t.ioOn : t.ioOff, 'success');
    } catch (err) {
        console.error('Invite-only toggle failed:', err);
        toggle.checked = !want;
        _impToast(t.ioFail, 'error');
    } finally {
        toggle.disabled = false;
    }
}

// An Excel file as CSV text (task #863 r2). A CSV saved from Excel writes a
// long phone number as "9.72524E+11" - the digits are gone before we ever
// see the file, and setting the column to Text afterwards does not bring them
// back. The .xlsx itself still holds the whole number, so it is read with
// rawNumbers: 972524212132 arrives intact. SheetJS is loaded only when an
// Excel file is actually chosen.
const SHEETJS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
function _importIsExcel(file) {
    return /\.(xlsx|xls)$/i.test(String(file && file.name || ''));
}
function _loadSheetJs() {
    if (window.XLSX) return Promise.resolve(window.XLSX);
    if (window.__sheetJsLoading) return window.__sheetJsLoading;
    window.__sheetJsLoading = new Promise((resolve, reject) => {
        const sc = document.createElement('script');
        sc.src = SHEETJS_URL;
        sc.onload = () => (window.XLSX ? resolve(window.XLSX) : reject(new Error('SheetJS missing')));
        sc.onerror = () => { window.__sheetJsLoading = null; reject(new Error('SheetJS failed to load')); };
        document.head.appendChild(sc);
    });
    return window.__sheetJsLoading;
}
async function _excelToCsvText(file) {
    const XLSX = await _loadSheetJs();
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array', cellDates: true });
    const ws = wb.Sheets[wb.SheetNames[0]];
    if (!ws) return '';
    return XLSX.utils.sheet_to_csv(ws, { rawNumbers: true, blankrows: false });
}

async function _importHandleFile(file) {
    const t = getImportStrings();
    _importState._lastFile = file;
    _importState.fileName = file.name || 'import.csv';
    try {
        let text;
        if (_importIsExcel(file)) {
            try { text = await _excelToCsvText(file); } catch (xErr) {
                console.error('Excel read failed:', xErr);
                _impToast(t.excelFail, 'error');
                return;
            }
        } else {
            text = await window.FileHelpers.readFile(file);
        }
        const parsed = window.FileHelpers.parseResponsesCsv(text, { hasHeader: _importState.hasHeader });
        if (!parsed.rows.length) {
            document.getElementById('importDetected').textContent = t.empty;
            return;
        }
        _importState.parsed = parsed;
        _importState.mapping = _impAutoMap(parsed.headers);
        document.getElementById('importDetected').textContent = t.detected(parsed.rows.length);
        _renderImportMapping();
        _importSetStep(2);
    } catch (err) {
        console.error('Import parse error:', err);
        _impToast(t.parseErr, 'error');
    }
}

// Guest-list mode looks for its own fields too (group name, tags) - #883,
// #935. The rows let a bare "Group" column be read by what it holds.
function _impAutoMap(headers) {
    const mapping = _impWithSeating(() => window.FileHelpers.autoMapResponseColumns(headers, _impFormData() || {},
        { guestlist: _importState.intent === 'guestlist', rows: _importState.parsed ? _importState.parsed.rows : null }));
    // The Last name row is offered for this file when it has such a column,
    // and stays while the host maps it away and back (task #1042).
    _importState.lastNameOffered = !!(mapping && mapping.guestLastName);
    return mapping;
}

function _importHeaderOptions(selected) {
    const t = getImportStrings();
    const heads = _importState.parsed.headers;
    let html = `<option value="__ignore__">${t.ignore}</option>`;
    heads.forEach(h => {
        html += `<option value="${_importEsc(h)}"${h === selected ? ' selected' : ''}>${_importEsc(h)}</option>`;
    });
    return html;
}

// One field per line - the field, the column it comes from, and whether it
// was found - instead of a two-column wall of labels and boxes (task #883).
function _importEnsureMapStyles() {
    if (document.getElementById('impMapStyles')) return;
    const st = document.createElement('style');
    st.id = 'impMapStyles';
    st.textContent = '.imp-map-list{border:1px solid #e9ecef;border-radius:12px;overflow:hidden;}'
        + '.imp-map-row{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.3fr) 1.6rem;align-items:center;gap:.75rem;padding:.55rem .85rem;border-bottom:1px solid #f1f3f5;background:#fff;}'
        + '.imp-map-row:last-child{border-bottom:0;}'
        + '.imp-map-row.is-mapped{background:#fbfefc;}'
        + '.imp-map-label{font-size:.85rem;font-weight:600;color:#212529;margin:0;display:flex;align-items:center;gap:.35rem;min-width:0;}'
        + '.imp-map-label .imp-map-hint{font-weight:400;opacity:.6;cursor:help;}'
        + '.imp-map-state{text-align:center;font-size:1rem;color:#adb5bd;}'
        + '.imp-map-row.is-mapped .imp-map-state{color:#198754;}'
        + '.imp-map-row .form-select{font-size:.85rem;}'
        + '.imp-map-selects{display:flex;flex-direction:column;gap:.35rem;min-width:0;}'
        + '@media (max-width:575.98px){.imp-map-row{grid-template-columns:minmax(0,1fr) 1.6rem;}.imp-map-row .form-select,.imp-map-row .imp-map-selects{grid-column:1 / -1;grid-row:2;}}';
    document.head.appendChild(st);
}
// `more` ({ attrs, selected }) is a second column read into the same field:
// a guest list's older Party members column, whose people are Guest name(s)
// too (task #1042), so it gets no row or label of its own.
function _importMapRowHtml(attrs, labelText, selected, required, hint, more) {
    const t = getImportStrings();
    const isOn = (v) => !!v && v !== '__ignore__';
    const mapped = isOn(selected) || !!(more && isOn(more.selected));
    let select = `<select class="form-select form-select-sm" ${attrs}>${_importHeaderOptions(isOn(selected) ? selected : null)}</select>`;
    if (more) select = `<div class="imp-map-selects">${select}<select class="form-select form-select-sm" ${more.attrs}>${_importHeaderOptions(isOn(more.selected) ? more.selected : null)}</select></div>`;
    return `
        <div class="imp-map-row${mapped ? ' is-mapped' : ''}">
            <label class="imp-map-label">${labelText}${required ? ' <span class="text-danger">*</span>' : ''}${hint ? ` <i class="bi bi-info-circle imp-map-hint js-hint-tip" tabindex="0" role="img" aria-label="${_importEsc(hint)}" title="${_importEsc(hint)}"></i>` : ''}</label>
            ${select}
            <span class="imp-map-state" title="${mapped ? '' : _importEsc(t.notInFile)}"><i class="bi ${mapped ? 'bi-check-circle-fill' : 'bi-dash-circle'}" aria-hidden="true"></i></span>
        </div>`;
}
function _importMapRow(fieldKey, labelText, selected, required, hint, more) {
    const moreHtml = more && { attrs: `id="import-map-${more.fieldKey}" data-field="${more.fieldKey}" aria-label="${_importEsc(labelText)} (2)"`, selected: more.selected };
    return _importMapRowHtml(`id="import-map-${fieldKey}" data-field="${fieldKey}" aria-label="${_importEsc(labelText)}"`, labelText, selected, required, hint, moreHtml);
}

function _renderImportMapping() {
    const t = getImportStrings();
    const fd = _impFormData() || {};
    const m = _importState.mapping;
    const step = document.querySelector('#importResponsesModal .import-step[data-step="2"]');
    const separate = fd.separateAdultsChildren === true;

    _importEnsureMapStyles();
    let rows = '';
    const guestlist = _importState.intent === 'guestlist';
    // Every import names its people in Guest name(s) and/or a Group name -
    // either one is enough for a row (task #935), so neither is starred. A
    // file of replies too, in the words the rest of the app uses (task #1042:
    // it said "Name", "Last name" and "Relationship / group").
    // A guest list in the older layout ("Name" + "Party members", #883) reads
    // its second column of people into Guest name(s) too: one row with two
    // column pickers, not a "Party members" row that repeated it (#1042).
    const membersCol = guestlist && m.partyMembers ? { fieldKey: 'partyMembers', selected: m.partyMembers } : null;
    rows += _importMapRow('guestName', t.fGuestNames, m.guestName, false, t.fGuestNamesHint, membersCol);
    // Task #890: a separate last-name column is joined onto the name. Offered
    // only when the file has one (the auto-mapper found it): otherwise it was
    // a second name row on every import.
    if (m.guestLastName || _importState.lastNameOffered) rows += _importMapRow('guestLastName', t.fLastName, m.guestLastName, false, t.fLastNameHint);
    if (!guestlist) rows += _importMapRow('groupName', t.fGroupName, m.groupName, false, t.fGroupNameHint);
    if (guestlist) {
        // A list of people to invite (task #883): only what matters BEFORE
        // anyone replies - who the invitation is addressed to, how to reach
        // them, how many it is for and which groups they are in. Meal,
        // relationship, reply date and the form's own questions are answers;
        // there are none yet.
        rows += _importMapRow('groupName', t.fGroupName, m.groupName, false, t.fGroupNameHint);
        rows += _importMapRow('phoneNumber', t.fPhone, m.phoneNumber, false);
        rows += _importMapRow('email', t.fEmail, m.email, false);
        if (separate) {
            rows += _importMapRow('adultCount', t.fAdults, m.adultCount, false);
            rows += _importMapRow('childrenCount', t.fChildren, m.childrenCount, false);
        } else {
            rows += _importMapRow('guestCount', t.fMax, m.guestCount, false, t.fGuestsAllowance);
        }
        rows += _importMapRow('tags', t.fTags, m.tags, false, t.fTagsHint);
        // A corporate event's own identity questions stay: company drives
        // "Group by company" seating before anyone has replied.
        if (fd.includeCompany === true) rows += _importMapRow('company', t.fCompany, m.company, false);
        if (fd.includeJobTitle === true) rows += _importMapRow('jobTitle', t.fJobTitle, m.jobTitle, false);
        if (fd.includeDepartment === true) rows += _importMapRow('department', t.fDepartment, m.department, false);
        // ...and a table, on an event that seats people: hosts seat invitees
        // before they reply (task #667 r2).
        if (_impSeatingOffered(fd)) rows += _importMapRow('tableNumber', t.fTable, m.tableNumber, false, t.fTableHint);
    }
    // Guest-list mode forces every row to "Awaiting reply" — an attendance
    // column would be ignored, so don't offer to map one.
    if (!guestlist) {
        rows += _importMapRow('attending', t.fAttending, m.attending, false);
    }
    if (!guestlist) {
    if (separate) {
        rows += _importMapRow('adultCount', t.fAdults, m.adultCount, false);
        rows += _importMapRow('childrenCount', t.fChildren, m.childrenCount, false);
    } else {
        rows += _importMapRow('guestCount', t.fGuests, m.guestCount, false);
    }
    if (fd.includeDietary !== false) rows += _importMapRow('dietary', t.fDietary, m.dietary, false);
    if (fd.includeRelationship !== false) rows += _importMapRow('relationship', t.fRelationship, m.relationship, false);
    rows += _importMapRow('tags', t.fTags, m.tags, false, t.fTagsHint);
    if (fd.includeEmail === true) rows += _importMapRow('email', t.fEmail, m.email, false);
    if (fd.includePhoneNumber !== false) rows += _importMapRow('phoneNumber', t.fPhone, m.phoneNumber, false);
    // Corporate questions (task #625 round 2). Only offered when the event
    // actually asks them, on the same principle as dietary/relationship above —
    // a wedding host should not be asked to map a Department column. The import
    // route allowlists these for the main guest, so a mapped column lands on the
    // response and shows in the delegate sheet's own Company/Job title columns.
    if (fd.includeCompany === true) rows += _importMapRow('company', t.fCompany, m.company, false);
    if (fd.includeJobTitle === true) rows += _importMapRow('jobTitle', t.fJobTitle, m.jobTitle, false);
    if (fd.includeDepartment === true) rows += _importMapRow('department', t.fDepartment, m.department, false);
    // Where each guest sits (task #667 round 2). Only on events that seat
    // people at all - the column SEATS them on the chart, so offering it where
    // there is no chart would promise something nothing acts on.
    if (_impSeatingOffered(fd)) {
        rows += _importMapRow('tableNumber', t.fTable, m.tableNumber, false, t.fTableHint);
    }
    rows += _importMapRow('submissionDate', t.fDate, m.submissionDate, false);
    }

    // Custom fields - the form's own questions: answers, so not on a guest list.
    const customFields = guestlist ? []
        : (Array.isArray(fd.customFields) ? fd.customFields : []).filter(f => f && f.id && f.type !== 'message');
    let customRows = '';
    customFields.forEach(f => {
        const sel = m.customFields[f.id] || '__ignore__';
        customRows += _importMapRowHtml(`id="import-cf-${_importEsc(f.id)}" data-cf="${_importEsc(f.id)}" aria-label="${_importEsc(f.label || f.id)}"`, _importEsc(f.label || f.id), sel, false);
    });

    step.innerHTML = `
        <h6 class="mb-1">${t.mHeading}</h6>
        <p class="text-muted small">${guestlist ? t.glMapHint : t.mHint}</p>
        <div class="imp-map-list">${rows}</div>
        ${customFields.length ? `<div class="mt-3"><div class="small fw-semibold text-muted mb-1">${t.fCustom}</div><div class="imp-map-list">${customRows}</div></div>` : ''}
        <div id="importAttValues" class="mt-2"></div>
        <div class="mt-3">
            <div class="small fw-semibold text-muted mb-1">${t.preview}</div>
            <div class="table-responsive" id="importMiniPreview"></div>
            ${guestlist ? '<div id="importGlCard"></div>' : ''}
        </div>
    `;

    // Re-render attendance value-map + preview when the attending column changes.
    const attSel = document.getElementById('import-map-attending');
    if (attSel) attSel.onchange = () => { _renderImportAttValues(); _renderImportMiniPreview(); };
    step.querySelectorAll('select').forEach(s => {
        s.addEventListener('change', _renderImportMiniPreview);
        // The tick follows the choice.
        s.addEventListener('change', () => {
            const row = s.closest('.imp-map-row');
            if (!row) return;
            const on = [...row.querySelectorAll('select')].some(x => x.value !== '__ignore__');
            row.classList.toggle('is-mapped', on);
            const icon = row.querySelector('.imp-map-state i');
            if (icon) icon.className = 'bi ' + (on ? 'bi-check-circle-fill' : 'bi-dash-circle');
        });
    });
    _renderImportAttValues();
    _renderImportMiniPreview();
}

function _importSelectedHeader(fieldKey) {
    const el = document.getElementById('import-map-' + fieldKey);
    if (!el || el.value === '__ignore__') return null;
    return el.value;
}

function _renderImportMiniPreview() {
    const t = getImportStrings();
    const container = document.getElementById('importMiniPreview');
    if (!container || !_importState.parsed) return;
    const fd = _impFormData() || {};
    const separate = fd.separateAdultsChildren === true;
    const guestlistMode = _importState.intent === 'guestlist';
    const cols = guestlistMode ? [[t.fGuestNames, 'guestName'], [t.fGroupName, 'groupName'], [t.fPhone, 'phoneNumber'], [t.fEmail, 'email']] : [[t.fGuestNames, 'guestName'], [t.fGroupName, 'groupName'], [t.fAttending, 'attending']];
    if (separate) cols.push([t.fAdults, 'adultCount'], [t.fChildren, 'childrenCount']);
    else cols.push([guestlistMode ? t.fMax : t.fGuests, 'guestCount']);
    if (guestlistMode) cols.push([t.fTags, 'tags']);
    if (!guestlistMode && fd.includeDietary !== false) cols.push([t.fDietary, 'dietary']);
    if (fd.includeCompany === true) cols.push([t.fCompany, 'company']);
    if (fd.includeJobTitle === true) cols.push([t.fJobTitle, 'jobTitle']);
    if (fd.includeDepartment === true) cols.push([t.fDepartment, 'department']);
    if (_impSeatingOffered(fd)) cols.push([t.fTable, 'tableNumber']);
    const active = cols.filter(([, key]) => _importSelectedHeader(key) || (key === 'guestName' && guestlistMode && _importSelectedHeader('partyMembers')));
    if (guestlistMode) _renderImportGlCard();
    if (!active.length) { container.innerHTML = ''; return; }
    const rows = _importState.parsed.rows.slice(0, 4);
    const head = active.map(([label]) => `<th class="small">${_importEsc(label)}</th>`).join('');
    const body = rows.map((r, i) => {
        const tds = active.map(([, key]) => {
            const h = _importSelectedHeader(key);
            let v = h ? (r[h] || '') : '';
            // A second names column (#1042) shows in the same cell, after the first.
            if (key === 'guestName') v = [_impGuestName(r), guestlistMode ? _impCell(r, 'partyMembers') : ''].filter(Boolean).join('; ');
            if (key === 'attending' && v) {
                const norm = window.FileHelpers.normalizeAttending(v);
                v = norm ? (t[norm] || v) : v;
            }
            return `<td class="small">${_importEsc(v)}</td>`;
        }).join('');
        // On a guest list a row is picked for the invitation card by a click.
        return guestlistMode ? `<tr data-gl-row="${i}" style="cursor:pointer">${tds}</tr>` : `<tr>${tds}</tr>`;
    }).join('');
    container.innerHTML = `<table class="table table-sm table-bordered mb-0"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
    if (guestlistMode) {
        container.querySelectorAll('tr[data-gl-row]').forEach(tr => {
            tr.onclick = () => { _importState.cardRow = Number(tr.dataset.glRow); _renderImportGlCard(); };
        });
    }
}

// The mapping step's invitation card (task #935): the row the host clicked in
// the preview table, else the first row that names anyone.
function _renderImportGlCard() {
    const box = document.getElementById('importGlCard');
    if (!box || !_importState.parsed) return;
    const rows = _importState.parsed.rows;
    const named = (r) => !!(_impGuestName(r) || _impCell(r, 'groupName'));
    let row = rows[_importState.cardRow];
    if (!row || !named(row)) row = rows.find(named);
    box.innerHTML = row ? _impInvitationCardHtml(_impRowInvitation(row)) : '';
}

function _impCell(row, fieldKey) {
    const h = _importSelectedHeader(fieldKey);
    return h ? String(row[h] || '').trim() : '';
}

/**
 * One guest-list row as an invitation (task #935): the Guest name(s) cell
 * (FileHelpers.parseGuestNames), the Group name, and - from a file in the
 * older layout - a Party members cell, whose people follow. Returns
 * { cell, contact } where contact is HouseholdNames.guestListContact's
 * { name, groupName?, members? } - what gets stored.
 *
 * A single entry with a party column, or one that is a family name ("The
 * Smith Family" under Guest name(s)), is read the way a stored contact is
 * (guestListColumns): the family name becomes the group, and a party column
 * that names the guest again ("Evelyn Cohen" + "Evelyn; Sam") does not list
 * them twice.
 */
function _impInvitation(namesCell, groupCell, membersCell) {
    const FH = window.FileHelpers;
    const HN = window.HouseholdNames;
    const cell = String(namesCell == null ? '' : namesCell).replace(/\s+/g, ' ').trim();
    let people = FH.parseGuestNames(cell, { group: groupCell }) || [];
    let group = HN.cleanGroupName(groupCell);
    const legacy = FH.parseHouseholdMembers(membersCell) || [];
    if (people.length === 1 && (legacy.length || HN.householdKind(people[0].name) === 'family')) {
        const cols = HN.guestListColumns({ name: people[0].name, members: legacy, groupName: group });
        people = cols.people;
        group = cols.groupName;
    } else if (legacy.length) {
        const key = (v) => HN.cleanName(v).toLowerCase();
        legacy.forEach((m) => {
            if (!people.some(p => key(p.name) === key(m.name) || HN.repeatsMainGuest(m, p.name))) people.push(m);
        });
    }
    return { cell, contact: HN.guestListContact(people, group) };
}
function _impRowInvitation(row) {
    return _impInvitation(_impGuestName(row), _impCell(row, 'groupName'), _impCell(row, 'partyMembers'));
}

// Which names find an invitation, and when the surname tip shows: the shared
// rule (HouseholdNames, task #935) the server's lookup uses too.
function _impInvitationPeople(contact) { return window.HouseholdNames.invitationPeople(contact); }
function _impLookupNames(contact) { return window.HouseholdNames.invitationLookupNames(contact); }
function _impNeedsSurnameTip(contact) { return window.HouseholdNames.invitationNeedsSurnameTip(contact); }

function _impInvitationCardHtml(inv) {
    const t = getImportStrings();
    const HN = window.HouseholdNames;
    const c = inv && inv.contact;
    if (!c || !c.name) return '';
    const bdi = (v) => `<bdi>${_importEsc(v)}</bdi>`;
    const keys = _impLookupNames(c);
    const groupOnly = !!c.groupName && !c.members && c.name === c.groupName;
    return `<div class="imp-gl-card" aria-live="polite">
        <div class="imp-gl-card-h">${_importEsc(t.pvTitle)}</div>
        <div class="imp-gl-line"><i class="bi bi-envelope" aria-hidden="true"></i><span><span class="text-muted">${_importEsc(t.pvAddressed)}</span> <bdi class="imp-gl-addr">${_importEsc(HN.invitationAddressee(c))}</bdi></span></div>
        <div class="imp-gl-line"><i class="bi bi-chat-left-text" aria-hidden="true"></i><span>${t.pvHi(bdi(HN.invitationGreeting(c)))}</span></div>
        <div class="imp-gl-line"><i class="bi bi-search" aria-hidden="true"></i><span>${keys.length ? `${_importEsc(t.pvFind)}<ul class="imp-gl-finds">${keys.map(k => `<li><bdi>“${_importEsc(k)}”</bdi></li>`).join('')}</ul>` : _importEsc(t.pvNoFind)}</span></div>
        ${groupOnly ? `<div class="imp-gl-line"><i class="bi bi-people" aria-hidden="true"></i><span>${_importEsc(t.pvAskNames)}</span></div>` : ''}
        ${_impNeedsSurnameTip(c) ? `<div class="imp-gl-tip"><i class="bi bi-lightbulb" aria-hidden="true"></i><span>${_importEsc(t.tipSurnames)}</span></div>` : ''}
    </div>`;
}

/**
 * The guest-list checks (task #935), for the review step and the typed
 * sheet: a Guest name(s) cell that reads as ONE person written surname-first
 * ("Smith, John"), the different-last-names tip (once, when any row needs
 * it), and invitations a guest could not tell apart by name - these rows
 * plus the event's existing guest list (imported rows and replies). Warnings,
 * never a block. entries: [{ cell, contact, email, phone }].
 */
function _impGuestListNotesHtml(entries, opts = {}) {
    const t = getImportStrings();
    const FH = window.FileHelpers;
    let html = '';
    const lastFirst = entries.filter(e => e.cell && FH.looksLikeLastFirst(e.cell, { group: e.contact && e.contact.groupName }));
    lastFirst.slice(0, 5).forEach((e) => {
        html += `<div class="imp-gl-note"><i class="bi bi-question-circle text-warning" aria-hidden="true"></i><span>${_importEsc(t.lastFirst(e.cell))}</span></div>`;
    });
    if (lastFirst.length > 5) html += `<div class="imp-gl-note"><span>${_importEsc(t.lastFirstMore(lastFirst.length - 5))}</span></div>`;
    if (opts.tip !== false && entries.some(e => e.contact && _impNeedsSurnameTip(e.contact))) {
        html += `<div class="imp-gl-tip"><i class="bi bi-lightbulb" aria-hidden="true"></i><span>${_importEsc(t.tipSurnames)}</span></div>`;
    }
    const fresh = entries.filter(e => e.contact && e.contact.name)
        .map(e => ({ name: e.contact.name, groupName: e.contact.groupName || '', email: e.email || '', phone: e.phone || '' }));
    const existing = (Array.isArray(_impResponses()) ? _impResponses() : []).filter(r => r && (r.guestName || r.inviteGroupName))
        .map(r => ({ name: r.guestName || '', groupName: r.inviteGroupName || '', email: r.email || '', phone: r.phoneNumber || r.phone || '' }));
    // The new rows go first, so a name shared with them is labelled as the
    // host wrote it now; a clash only among existing guests is not this
    // import's to report.
    const labels = new Set(fresh.map(r => String(r.groupName || r.name).replace(/\s+/g, ' ').trim()));
    const same = FH.sameNameInvitations(fresh.concat(existing)).filter(s => labels.has(s.name));
    if (same.length) {
        html += `<div class="imp-gl-warn"><i class="bi bi-exclamation-triangle me-1" aria-hidden="true"></i>${_importEsc(t.sameName)}
            <div class="mt-1 fw-semibold">${same.slice(0, 8).map(s => `<bdi>${_importEsc(s.name)}</bdi> (${s.count})`).join(' · ')}${same.length > 8 ? ' …' : ''}</div></div>`;
    }
    return html;
}

function _renderImportAttValues() {
    const t = getImportStrings();
    const container = document.getElementById('importAttValues');
    if (!container) return;
    // Guest-list mode ignores the attendance column entirely (everything
    // imports as "Awaiting reply") — nothing to disambiguate.
    if (_importState.intent === 'guestlist') { container.innerHTML = ''; _importState.attOverrides = {}; return; }
    const attHeader = _importSelectedHeader('attending');
    if (!attHeader) { container.innerHTML = ''; _importState.attOverrides = {}; return; }
    // distinct raw values that don't auto-normalize
    const distinct = [];
    const seen = new Set();
    (_importState.parsed?.rows || []).forEach(r => {
        const v = (r[attHeader] || '').trim();
        if (!v || seen.has(v.toLowerCase())) return;
        seen.add(v.toLowerCase());
        if (window.FileHelpers.normalizeAttending(v) === null) distinct.push(v);
    });
    if (!distinct.length) { container.innerHTML = ''; return; }
    let html = `<div class="alert alert-warning py-2 px-3 small mb-0"><div class="fw-semibold mb-2"><i class="bi bi-question-circle me-1"></i>${t.attHeading}</div><div class="small text-muted mb-2">${t.attHint}</div><div class="row">`;
    distinct.slice(0, 12).forEach((v, i) => {
        html += `
            <div class="col-12 col-md-6 mb-2 d-flex align-items-center gap-2">
                <span class="badge bg-light text-dark border flex-shrink-0" style="max-width:8rem;overflow:hidden;text-overflow:ellipsis;">${_importEsc(v)}</span>
                <select class="form-select form-select-sm import-att-override" data-rawval="${_importEsc(v)}">
                    <option value="yes">${t.yes}</option>
                    <option value="no">${t.no}</option>
                    <option value="maybe">${t.maybe}</option>
                    <option value="pending">${t.pending}</option>
                    <option value="__skip__">${t.skipVal}</option>
                </select>
            </div>`;
    });
    html += '</div></div>';
    container.innerHTML = html;
}

function _collectAttOverrides() {
    const map = {};
    document.querySelectorAll('#importAttValues .import-att-override').forEach(sel => {
        map[String(sel.dataset.rawval).toLowerCase()] = sel.value;
    });
    return map;
}

// The guest's name as it will be saved: the name column, plus the last-name
// column when the file has one (task #890).
function _impGuestName(row) {
    const pick = (fieldKey) => { const h = _importSelectedHeader(fieldKey); return h ? (row[h] || '') : ''; };
    return window.FileHelpers.joinNameParts(pick('guestName'), pick('guestLastName'));
}

function buildImportPayload() {
    const fd = _impFormData() || {};
    const rows = _importState.parsed.rows;
    const separate = fd.separateAdultsChildren === true;
    const attHeader = _importSelectedHeader('attending');
    const overrides = _collectAttOverrides();
    const dietaryOptions = Array.isArray(fd.dietaryOptions) ? fd.dietaryOptions : [];

    const get = (row, fieldKey) => {
        const h = _importSelectedHeader(fieldKey);
        return h ? (row[h] || '').trim() : '';
    };
    // custom field header map (live from selects)
    const cfSelects = Array.from(document.querySelectorAll('#importResponsesModal select[data-cf]'));

    const out = [];
    rows.forEach(row => {
        const guestName = _impGuestName(row);
        const payload = { guestName };
        // A guest list's people, for a blank Max guests (task #935).
        let headcount = null;

        if (_importState.intent === 'guestlist') {
            // Guest-list import: every invitee starts as awaiting-reply. The
            // server derives each row's invite-only allowance from its counts.
            payload.attending = 'pending';
            // Who the invitation is addressed to and who is on it (task #935):
            // the stored name is the first person, or the group when no one is
            // listed - a row with neither stays nameless and is skipped as "no
            // name". The people after the first ride in individualNames
            // ("Dana; Ari (child)" in Messaging's format), never the main
            // guest again - also not by first name only (#883).
            const inv = _impRowInvitation(row);
            const c = inv.contact;
            payload.guestName = c.name;
            if (c.groupName) payload.groupName = c.groupName;
            const others = (c.members || []).slice(1).filter(mm => !window.HouseholdNames.repeatsMainGuest(mm, c.name));
            if (others.length) payload.individualNames = others.map(mm => ({ name: mm.name, guestType: mm.guestType === 'child' ? 'child' : 'adult' }));
            const people = _impInvitationPeople(c);
            if (people.length) headcount = { adults: people.filter(p => p.guestType !== 'child').length, children: people.filter(p => p.guestType === 'child').length };
            // Which groups they are in (#883): "family, vip".
            const tags = window.FileHelpers.parseContactTags(get(row, 'tags'));
            if (tags && tags.length) payload.tags = tags;
        } else {
            // A file of replies reads its Guest name(s) and Group name the same
            // way (task #1042): "Sarah; Michael" is two people on one reply.
            const inv = _impRowInvitation(row);
            const c = inv.contact;
            if (c.name) payload.guestName = c.name;
            if (c.groupName) payload.groupName = c.groupName;
            const others = (c.members || []).slice(1).filter(mm => !window.HouseholdNames.repeatsMainGuest(mm, c.name));
            if (others.length) payload.individualNames = others.map(mm => ({ name: mm.name, guestType: mm.guestType === 'child' ? 'child' : 'adult' }));
            const tags = window.FileHelpers.parseContactTags(get(row, 'tags'));
            if (tags && tags.length) payload.tags = tags;
        }
        if (_importState.intent !== 'guestlist' && attHeader) {
            const raw = (row[attHeader] || '').trim();
            let att = window.FileHelpers.normalizeAttending(raw);
            if (att === null && raw) {
                const ov = overrides[raw.toLowerCase()];
                if (ov === '__skip__') return; // host chose to drop these rows
                if (ov) att = ov;
            }
            if (att) payload.attending = att; // omitted → server stores 'pending' (awaiting a reply)
        }

        // A blank Max guests on a guest list is the number of people named
        // (task #935); a group with no names yet keeps the server's default.
        if (separate) {
            const a = get(row, 'adultCount'); if (a) payload.adultCount = a;
            const c = get(row, 'childrenCount'); if (c) payload.childrenCount = c;
            if (!a && !c && headcount) {
                if (headcount.adults) payload.adultCount = String(headcount.adults);
                if (headcount.children) payload.childrenCount = String(headcount.children);
            }
        } else {
            const g = get(row, 'guestCount'); if (g) payload.guestCount = g;
            else if (headcount) payload.guestCount = String(headcount.adults + headcount.children);
        }
        // "Use the number of names as Max guests" on the preview: a number
        // below the people the row names becomes their count.
        if (_importState.maxFromNames && headcount) {
            const named = headcount.adults + headcount.children;
            if (separate) {
                const total = (parseInt(payload.adultCount, 10) || 0) + (parseInt(payload.childrenCount, 10) || 0);
                if (total < named) { payload.adultCount = String(headcount.adults || 1); payload.childrenCount = String(headcount.children); }
            } else if ((parseInt(payload.guestCount, 10) || 0) < named) {
                payload.guestCount = String(named);
            }
        }
        const diet = get(row, 'dietary'); if (diet) payload.dietary = window.FileHelpers.snapDietaryToOptions(diet, dietaryOptions);
        const rel = get(row, 'relationship'); if (rel) payload.relationship = rel;
        const email = get(row, 'email'); if (email) payload.email = email;
        const phone = get(row, 'phoneNumber'); if (phone) payload.phoneNumber = phone;
        const date = get(row, 'submissionDate'); if (date) payload.submissionDate = date;
        // The table is NOT sent to the import endpoint - it is not a property of
        // the response, it is a seat on the chart. Carried alongside the payload
        // and applied after the rows have ids (task #667 round 2).
        const table = window.FileHelpers.normalizeTableAssignment(get(row, 'tableNumber'));
        if (table) payload._tableName = table;
        // Corporate questions — `get` returns null for a field the mapping step
        // never rendered, so nothing is sent for an event that doesn't ask them.
        ['company', 'jobTitle', 'department'].forEach(k => {
            const v = get(row, k); if (v) payload[k] = v;
        });

        const customFields = {};
        cfSelects.forEach(sel => {
            if (sel.value === '__ignore__') return;
            const val = (row[sel.value] || '').trim();
            if (val) customFields[sel.dataset.cf] = val;
        });
        if (Object.keys(customFields).length) payload.customFields = customFields;

        out.push(payload);
    });
    return out;
}

async function _postImportChunked(rows, mode, dryRun) {
    const CHUNK = 2000;
    const agg = { received: 0, imported: 0, updated: 0, skippedDuplicate: 0, invalid: 0,
        phonesFormatted: 0, phonesKeptAsTyped: 0, phonesLostDigits: 0, belowNames: 0 };
    const batchIds = [];
    // Per-row outcomes, re-indexed onto the WHOLE file rather than the chunk,
    // so the caller can pair a row with the response it became (task #667 r2).
    const results = [];
    let ok = true, errorMsg = '';
    for (let i = 0; i < rows.length; i += CHUNK) {
        // `_tableName` is ours, not the endpoint's - the table is a seat on the
        // chart, not a field of the response.
        const chunk = rows.slice(i, i + CHUNK).map(({ _tableName, ...r }) => r);
        let data = {};
        try {
            const res = await window.AuthedFetch.fetch(`/api/forms/${_impFormId()}/responses/import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rows: chunk, mode, dryRun, dedupe: { fuzzyMode: 'unique', nameMatching: true } })
            });
            data = await res.json().catch(() => ({}));
            if (!res.ok || data.success === false) {
                ok = false;
                errorMsg = (data && data.error) || `HTTP ${res.status}`;
                if (data && data.summary) Object.keys(agg).forEach(k => agg[k] += (data.summary[k] || 0));
                if (data && data.importBatchId) batchIds.push(data.importBatchId);
                break;
            }
        } catch (e) {
            ok = false; errorMsg = e.message || String(e); break;
        }
        if (data.summary) Object.keys(agg).forEach(k => agg[k] += (data.summary[k] || 0));
        if (Array.isArray(data.results)) data.results.forEach(r => results.push({ ...r, index: (r.index || 0) + i }));
        if (!dryRun && data.importBatchId) batchIds.push(data.importBatchId);
    }
    return { ok, errorMsg, summary: agg, batchIds, results };
}

async function _importValidateMappingAndPreview() {
    const t = getImportStrings();
    // No parsed file (modal reset, or Next/mode change before a file loaded):
    // back to step 1 instead of throwing on parsed.rows (dashboard-v2 client
    // error, 2026-10-06).
    if (!_importState || !_importState.parsed) { _importSetStep(1); return; }
    const st = _importState;
    // A guest list names an invitation by its people or its group (task #935).
    const guestlist = st.intent === 'guestlist';
    if (!_importSelectedHeader('guestName') && !_importSelectedHeader('groupName')) {
        _impToast(guestlist ? t.nameOrGroupReq : t.nameReq, 'error');
        return;
    }
    const step = document.querySelector('#importResponsesModal .import-step[data-step="3"]');
    step.innerHTML = `<div class="text-center py-4 text-muted"><div class="spinner-border spinner-border-sm me-2"></div>${t.running}</div>`;
    _importSetStep(3);

    const rows = buildImportPayload();
    _importState._payload = rows;
    const res = await _postImportChunked(rows, _importState.mode, true);
    if (_importState !== st) return; // modal closed/reset during the dry run
    _importState._unknownTags = await _impUnknownTags(rows);
    if (_importState !== st) return;
    _importState._glNotes = guestlist ? _impReviewNotes(res.results) : '';
    _importState._derivedMax = guestlist ? [] : (res.results || []).filter(r => r && r.derivedMax)
        .map(r => ({ name: (rows[r.index] && (rows[r.index].guestName || rows[r.index].groupName)) || '—', max: r.derivedMax }));
    _importState._belowNames = (res.results || []).filter(r => r && r.belowNames)
        .map(r => ({ name: (rows[r.index] && (rows[r.index].guestName || rows[r.index].groupName)) || '—', ...r.belowNames }));
    _renderImportPreview(res.summary);
}

// The review step's guest-list checks (task #935) over the rows that will
// become NEW invitations: a row the dry run matched to an existing guest
// (skipped or updating it) is that guest, not a second one by the same name.
function _impReviewNotes(results) {
    const notNew = new Set((results || []).filter(r => r && /skip|update|invalid/.test(String(r.status || ''))).map(r => r.index));
    const entries = [];
    (_importState.parsed.rows || []).forEach((row, i) => {
        const inv = _impRowInvitation(row);
        entries.push({
            cell: inv.cell,
            contact: notNew.has(i) ? null : inv.contact,
            email: _impCell(row, 'email'),
            phone: _impCell(row, 'phoneNumber')
        });
    });
    // A Party members column the host mapped by hand that holds categories
    // ("Familia" x 72): the auto-mapper never picks one (3d), but a manual
    // pick would save each value as a person on the invitation.
    const t = getImportStrings();
    let warn = '';
    const h = _importSelectedHeader('partyMembers');
    const cells = h ? (_importState.parsed.rows || []).map(r => r && r[h]) : [];
    if (h && window.FileHelpers._holdsCategories(cells)) {
        const counts = new Map();
        cells.forEach(v => { const k = String(v == null ? '' : v).trim(); if (k) counts.set(k, (counts.get(k) || 0) + 1); });
        const sample = [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
        warn = `<div class="imp-gl-warn"><i class="bi bi-exclamation-triangle me-1" aria-hidden="true"></i>${_importEsc(t.catCol(`“${h}”`, sample))}</div>`;
    }
    return warn + _impGuestListNotesHtml(entries);
}

// Tags in the file that no part or website event uses (#883): said before the
// import, since a misspelt tag silently unlocks nothing on that guest's link.
async function _impUnknownTags(rows) {
    const used = new Map();
    rows.forEach(r => (r.tags || []).forEach(tg => { const k = String(tg).trim().toLowerCase(); if (k && !used.has(k)) used.set(k, tg); }));
    if (!used.size || !window.TagPicker || !window.TagPicker._knownTags) return [];
    try {
        const known = await window.TagPicker._knownTags(_impFormId());
        const ok = new Set((known || []).map(x => String(x.tag).trim().toLowerCase()));
        return [...used.entries()].filter(([k]) => !ok.has(k)).map(([, v]) => v);
    } catch (_) { return []; }
}

function _renderImportPreview(summary) {
    const t = getImportStrings();
    const step = document.querySelector('#importResponsesModal .import-step[data-step="3"]');
    const s = summary || {};
    const chip = (icon, cls, text) => `<div class="d-flex align-items-center gap-2 mb-1"><i class="bi ${icon} ${cls}"></i><span>${text}</span></div>`;
    let counts = '';
    counts += chip('bi-check-circle-fill', 'text-success', t.pImport(s.imported || 0));
    if (_importState.mode === 'update-existing') counts += chip('bi-arrow-repeat', 'text-primary', t.pUpdate(s.updated || 0));
    if (s.skippedDuplicate) counts += chip('bi-files', 'text-secondary', t.pSkip(s.skippedDuplicate));
    if (s.invalid) counts += chip('bi-exclamation-triangle', 'text-warning', t.pInvalid(s.invalid));
    // Phones (task #863): what the import will do to them, before it does it.
    if (s.phonesFormatted && t.pPhonesFixed) counts += chip('bi-telephone', 'text-success', t.pPhonesFixed(s.phonesFormatted));
    if (s.phonesKeptAsTyped && t.pPhonesKept) counts += chip('bi-telephone', 'text-secondary', t.pPhonesKept(s.phonesKeptAsTyped));
    if (s.phonesLostDigits && t.pPhonesLost) counts += chip('bi-exclamation-triangle', 'text-warning', t.pPhonesLost(s.phonesLostDigits));
    const unknownTags = _importState._unknownTags || [];
    if (unknownTags.length) counts += chip('bi-tag', 'text-warning', _importEsc(t.pUnknownTags(unknownTags.map(x => '“' + x + '”').join(', '))));
    // More people named than Max guests (identity audit 2026-10-22): the
    // guest's form shows only that many. Named, with one tap to fix.
    const below = (_importState._belowNames || []);
    if (below.length && t.pBelowNames) {
        const rowsHtml = below.slice(0, 5).map(b => `<div class="small text-muted" dir="auto">${_importEsc(t.belowLine(b.name, b.listed, b.max, t.fMax))}</div>`).join('')
            + (below.length > 5 ? `<div class="small text-muted">+${below.length - 5}</div>` : '');
        counts += `<div class="d-flex align-items-start gap-2 mb-1" id="importBelowNames"><i class="bi bi-people text-warning mt-1"></i><div>
            <div>${_importEsc(t.pBelowNames(below.length, t.fMax))}</div>${rowsHtml}
            <button type="button" class="btn btn-sm btn-outline-secondary mt-1 py-0" id="importBelowNamesFix">${_importEsc(t.belowFix(t.fMax))}</button></div></div>`;
    }

    // A file of replies with no answer on a row (chat 3dbb5d64): that row's
    // number becomes the guest's Max guests. Said here, because a column
    // mapped by mistake would otherwise become every guest's limit unseen;
    // the ones above the form's own limit are named.
    const derived = (_importState._derivedMax || []);
    if (derived.length && t.pDerivedMax) {
        const fd = _impFormData() || {};
        const formMax = fd.setMaxGuests === true ? parseInt(fd.maxGuests, 10) : NaN;
        const above = Number.isFinite(formMax) && formMax > 0 ? derived.filter(d => d.max > formMax) : [];
        const aboveHtml = above.length ? `<div class="small text-muted mt-1">${_importEsc(t.derivedAbove(formMax))}</div>`
            + above.slice(0, 5).map(d => `<div class="small text-muted" dir="auto">${_importEsc(d.name + ': ' + d.max)}</div>`).join('')
            + (above.length > 5 ? `<div class="small text-muted">+${above.length - 5}</div>` : '') : '';
        counts += `<div class="d-flex align-items-start gap-2 mb-1" id="importDerivedMax"><i class="bi bi-person-lines-fill ${above.length ? 'text-warning' : 'text-secondary'} mt-1"></i><div>
            <div>${_importEsc(t.pDerivedMax(derived.length, t.fMax))}</div>${aboveHtml}</div></div>`;
    }

    // Tools upsell (imports are never hidden — this promotes the paid organizing
    // tools, it never threatens to hide the host's own data).
    const isPaid = _impIsPaid();
    const upsell = isPaid ? '' : `
        <div class="alert alert-info mt-3 mb-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div><div class="fw-semibold">${t.toolsTitle}</div><div class="small">${t.toolsBody}</div></div>
            <button type="button" class="btn btn-sm btn-primary" onclick="window.PaymentManager?.showUpgradeModal('${_impFormId()}','responses','basic')">${t.upgrade}</button>
        </div>`;

    step.innerHTML = `
        <h6 class="mb-3">${t.pHeading}</h6>
        <div class="mb-3">${counts}</div>
        ${_importState.intent === 'guestlist' && _importState._glNotes ? `<div class="imp-gl-notes mb-3" id="importGlNotes">${_importState._glNotes}</div>` : ''}
        <div class="mb-2">
            <label class="form-label small mb-1">${t.mode}</label>
            <select class="form-select form-select-sm" id="importModeSelect" style="max-width:20rem;">
                <option value="skip-dupes"${_importState.mode === 'skip-dupes' ? ' selected' : ''}>${t.modeSkip}</option>
                <option value="update-existing"${_importState.mode === 'update-existing' ? ' selected' : ''}>${t.modeUpdate}</option>
                <option value="import-all"${_importState.mode === 'import-all' ? ' selected' : ''}>${t.modeAll}</option>
            </select>
        </div>
        ${upsell}
    `;
    const belowFix = document.getElementById('importBelowNamesFix');
    if (belowFix) belowFix.onclick = () => { _importState.maxFromNames = true; _importValidateMappingAndPreview(); };
    document.getElementById('importModeSelect').onchange = (e) => {
        _importState.mode = e.target.value;
        _importValidateMappingAndPreview(); // re-run preview with the new mode
    };
}

async function _importCommit(opts = {}) {
    const t = getImportStrings();
    if (_importState.busy) return null;
    _importState.busy = true;
    // The visible primary button spins: Import on Preview, "Add to guest
    // list" when guests were typed in.
    const btn = document.getElementById(_importState.step === 3 ? 'importCommitBtn' : 'importNextBtn');
    const backBtn = document.getElementById('importBackBtn');
    const original = btn.innerHTML;
    btn.disabled = true; backBtn.disabled = true;
    btn.innerHTML = `<span class="spinner-border spinner-border-sm me-1"></span>${t.importing}`;

    let res;
    try {
        const rows = _importState._payload && _importState._payload.length ? _importState._payload : buildImportPayload();
        res = await _postImportChunked(rows, _importState.mode, false);
        _importState.committedBatchIds = res.batchIds || [];
        // Seat whoever the file gave a table (task #667 round 2). Done AFTER the
        // rows are written, because a seat needs a responseId. A row that was
        // SKIPPED as a duplicate is seated too - it matched somebody who had
        // already replied, which is the case this whole feature is for.
        res.seating = await _applyImportedTables(rows, res.results);
    } catch (e) {
        // Anything thrown used to leave the button spinning on "Importing..."
        // and the wizard locked busy until a page reload (task #918).
        res = { ok: false, errorMsg: e.message || String(e), summary: {}, batchIds: _importState.committedBatchIds || [] };
    } finally {
        _importState.busy = false;
        btn.disabled = false; backBtn.disabled = false;
        btn.innerHTML = original;
    }

    if (!opts.quiet) {
        _renderImportResult(res);
        _importSetStep(4);
    }

    // Refresh the responses table + analytics + upgrade banner.
    await _impReload();
    return res;
}

/**
 * Seat the imported guests at the tables their file named (task #667 round 2).
 *
 * The table is deliberately NOT stored on the response: "where this person
 * sits" already has a home, the seating chart, and that is what check-in, the
 * seating tab and the {{table_number}} tag all read. So the import writes real
 * seats, and a table name with no table behind it creates one.
 *
 * Returns null when the file had no table column, so the result screen stays
 * silent about seating on every ordinary import.
 */
async function _applyImportedTables(rows, results) {
    const byIndex = new Map();
    (results || []).forEach(r => { if (r && typeof r.index === 'number') byIndex.set(r.index, r); });
    const assignments = [];
    (rows || []).forEach((row, i) => {
        const name = row && row._tableName;
        if (!name) return;
        const r = byIndex.get(i);
        const responseId = r && (r.responseId || r.matchedResponseId);
        if (!responseId) return;
        assignments.push({ responseId, tableName: name });
    });
    if (!assignments.length) return null;

    const agg = { seated: 0, tablesCreated: 0, overCapacity: [], failed: 0 };
    for (let i = 0; i < assignments.length; i += 500) {
        try {
            const res = await window.AuthedFetch.fetch(`/api/seating/${_impFormId()}/assign-by-name`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ assignments: assignments.slice(i, i + 500), source: 'responses-import' })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || data.success === false) { agg.failed += Math.min(500, assignments.length - i); continue; }
            agg.seated += data.seated || 0;
            agg.tablesCreated += data.tablesCreated || 0;
            if (Array.isArray(data.overCapacity)) agg.overCapacity = data.overCapacity;
        } catch (e) {
            agg.failed += Math.min(500, assignments.length - i);
        }
    }
    agg.requested = assignments.length;
    return agg;
}

function _renderImportResult(res) {
    const t = getImportStrings();
    const step = document.querySelector('#importResponsesModal .import-step[data-step="4"]');
    const s = res.summary || {};
    const hasUndo = (res.batchIds && res.batchIds.length);
    // Seating, only when the file actually had a Table column.
    const seat = res.seating;
    let seatLine = '';
    if (seat) {
        seatLine = `<li><i class="bi bi-grid-3x3-gap text-success me-1"></i>${t.rSeated(seat.seated, seat.tablesCreated)}</li>`;
        if (seat.failed) seatLine += `<li><i class="bi bi-exclamation-triangle text-warning me-1"></i>${t.rSeatFailed(seat.failed)}</li>`;
        (seat.overCapacity || []).slice(0, 3).forEach(o => {
            seatLine += `<li><i class="bi bi-exclamation-circle text-warning me-1"></i>${t.rSeatOver(o.name, o.seated, o.capacity)}</li>`;
        });
    }
    const icon = res.ok ? 'bi-check-circle-fill text-success' : 'bi-exclamation-triangle-fill text-warning';
    const heading = res.ok ? t.rHeading : t.failed;
    step.innerHTML = `
        <div class="text-center py-2">
            <i class="bi ${icon}" style="font-size:2.5rem;"></i>
            <h6 class="mt-2">${heading}</h6>
        </div>
        ${!res.ok ? `<div class="alert alert-warning small">${_importEsc(res.errorMsg || '')}${hasUndo ? '<div class="mt-1">' + t.partial + '</div>' : ''}</div>` : ''}
        <ul class="list-unstyled small mb-3">
            <li><i class="bi bi-check-circle text-success me-1"></i>${t.rImported(s.imported || 0)}</li>
            ${s.updated ? `<li><i class="bi bi-arrow-repeat text-primary me-1"></i>${t.rUpdated(s.updated)}</li>` : ''}
            ${s.skippedDuplicate ? `<li><i class="bi bi-files text-secondary me-1"></i>${t.rSkipped(s.skippedDuplicate)}</li>` : ''}
            ${s.invalid ? `<li><i class="bi bi-exclamation-triangle text-warning me-1"></i>${t.rInvalid(s.invalid)}</li>` : ''}
            ${seatLine}
        </ul>
        <div class="d-flex gap-2 justify-content-center">
            ${hasUndo ? `<button type="button" class="btn btn-sm btn-outline-danger" id="importUndoBtn"><i class="bi bi-arrow-counterclockwise me-1"></i>${t.undo}</button>` : ''}
            <button type="button" class="btn btn-sm btn-primary" data-bs-dismiss="modal">${t.close}</button>
        </div>
    `;
    const undoBtn = document.getElementById('importUndoBtn');
    if (undoBtn) undoBtn.onclick = () => _importUndo(undoBtn);
}

async function _importUndo(btn) {
    const t = getImportStrings();
    const ids = _importState.committedBatchIds || [];
    if (!ids.length) return;
    btn.disabled = true;
    const original = btn.innerHTML;
    btn.innerHTML = `<span class="spinner-border spinner-border-sm me-1"></span>${t.undoing}`;
    let ok = true;
    for (const id of ids) {
        try {
            const res = await window.AuthedFetch.fetch(`/api/forms/${_impFormId()}/responses/import/${encodeURIComponent(id)}`, { method: 'DELETE' });
            if (!res.ok) ok = false;
        } catch (e) { ok = false; }
    }
    _importState.committedBatchIds = [];
    await _impReload();
    _impToast(ok ? t.undone : t.undoFail, ok ? 'success' : 'error');
    const modal = bootstrap.Modal.getInstance(document.getElementById('importResponsesModal'));
    if (modal) modal.hide();
}

/**
 * Import batches still sitting in this form's responses, newest group first.
 * Derived from `_impResponses()` rather than a query: imported rows are never
 * hidden by the view-cap, so the loaded list already holds every one of them
 * and this costs no extra reads. A file over 2000 rows commits as several
 * batches seconds apart (see _postImportChunked) — those are ONE import to the
 * host, so batches within two minutes of each other merge into one group and
 * are removed together.
 */
function _importPreviousBatches() {
    const rows = Array.isArray(_impResponses()) ? _impResponses() : [];
    const byBatch = new Map();
    rows.forEach(r => {
        if (!r || r.importedEntry !== true || !r.importBatchId) return;
        const at = (r.timestamp && r.timestamp.seconds) ? r.timestamp.seconds * 1000 : 0;
        const b = byBatch.get(r.importBatchId) || { id: r.importBatchId, removable: 0, kept: 0, first: 0, last: 0 };
        // A guest who replied on an imported row owns it now — the server's
        // undo skips those, so don't count them as removable here either.
        if (r.guestConfirmedAt) b.kept++; else b.removable++;
        if (at) {
            b.first = b.first ? Math.min(b.first, at) : at;
            b.last = Math.max(b.last, at);
        }
        byBatch.set(r.importBatchId, b);
    });

    const GAP_MS = 2 * 60 * 1000;
    const groups = [];
    Array.from(byBatch.values()).sort((a, b) => a.first - b.first).forEach(b => {
        const prev = groups[groups.length - 1];
        if (prev && b.first && prev.last && (b.first - prev.last) <= GAP_MS) {
            prev.ids.push(b.id);
            prev.removable += b.removable;
            prev.kept += b.kept;
            prev.last = Math.max(prev.last, b.last);
        } else {
            groups.push({ ids: [b.id], removable: b.removable, kept: b.kept, at: b.first, last: b.last });
        }
    });
    return groups.reverse();
}

/**
 * Render the "Previous imports" list on the upload step. This is the only
 * place a host can undo an import after closing the wizard — the result
 * screen's Undo button is gone the moment the modal closes.
 */
function _renderImportPrevious() {
    const host = document.getElementById('importPrevious');
    if (!host) return;
    const t = getImportStrings();
    const groups = _importPreviousBatches();
    if (!groups.length) { host.innerHTML = ''; return; }
    host.innerHTML = `
        <div class="border rounded p-2 mb-3">
            <div class="small fw-semibold mb-2"><i class="bi bi-clock-history me-1"></i>${t.prevHeading}</div>
            ${groups.map((g, i) => `
                <div class="d-flex justify-content-between align-items-center gap-2 ${i ? 'mt-2 pt-2 border-top' : ''}">
                    <div class="small">
                        ${g.at ? `<div>${_importEsc(new Date(g.at).toLocaleString())}</div>` : ''}
                        <div class="text-muted">${t.prevGuests(g.removable)}${g.kept ? ' &middot; ' + t.prevKept(g.kept) : ''}</div>
                    </div>
                    <button type="button" class="btn btn-sm btn-outline-danger flex-shrink-0" data-import-remove="${i}"${g.removable ? '' : ' disabled'}>
                        <i class="bi bi-trash me-1"></i>${t.prevRemove}
                    </button>
                </div>
            `).join('')}
        </div>
    `;
    host.querySelectorAll('[data-import-remove]').forEach(btn => {
        btn.onclick = () => _importRemovePreviousGroup(groups[Number(btn.dataset.importRemove)], btn);
    });
}

async function _importRemovePreviousGroup(group, btn) {
    const t = getImportStrings();
    if (!group || !group.ids.length || !group.removable) return;
    if (!confirm(t.prevConfirm(group.removable))) return;
    const original = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = `<span class="spinner-border spinner-border-sm me-1"></span>${t.prevRemoving}`;
    let ok = true;
    for (const id of group.ids) {
        try {
            const res = await window.AuthedFetch.fetch(`/api/forms/${_impFormId()}/responses/import/${encodeURIComponent(id)}`, { method: 'DELETE' });
            if (!res.ok) ok = false;
        } catch (e) { ok = false; }
    }
    // Reload first — the list below is rebuilt from _impResponses().
    await _impReload();
    btn.innerHTML = original;
    btn.disabled = false;
    _renderImportPrevious();
    _impToast(ok ? t.prevRemoved : t.prevFail, ok ? 'success' : 'error');
}

function _importCsvCell(v) {
    const s = String(v == null ? '' : v);
    return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

// A guest list's template is its six columns (task #935): one person, a
// family with its people, and a group whose people are not known yet.
const GUESTLIST_TEMPLATE = [
    ['Guest name(s)', 'Group name', 'Phone', 'Email', 'Max guests', 'Tags'],
    ['Jane Doe', '', '+15558010250', 'jane@example.com', '1', 'friends'],
    ['Sarah Smith; Michael Smith; Toby Smith (child)', 'The Smith Family', '+15558010251', 'sarah@example.com', '', 'family'],
    ['', 'The Cohen Family', '+15558010252', '', '4', 'family']
];
function _downloadResponsesTemplate() {
    const fd = _impFormData() || {};
    if (_importState && _importState.intent === 'guestlist') {
        _importSaveCsv(GUESTLIST_TEMPLATE, 'quikrsvp-guest-list-template.csv');
        return;
    }
    // Guest name(s) | Group name first, as the mapping step names them (task
    // #1042 r3): a reply for two people is one row, ";" between them.
    const cols = ['Guest name(s)', 'Group name', 'RSVP'];
    const ex1 = ['Jane Cohen; Ben Cohen', 'The Cohen Family', 'Yes'];
    const ex2 = ['David Levy', '', 'No'];
    if (fd.separateAdultsChildren === true) { cols.push('Adults', 'Children'); ex1.push('2', '1'); ex2.push('1', '0'); }
    else { cols.push('Guests'); ex1.push('2'); ex2.push('1'); }
    if (fd.includeDietary !== false) { cols.push('Meal'); ex1.push('Vegetarian'); ex2.push(''); }
    if (fd.includeRelationship !== false) { cols.push('Relationship'); ex1.push('Friend'); ex2.push('Family'); }
    if (fd.includeEmail === true) { cols.push('Email'); ex1.push('jane@example.com'); ex2.push('david@example.com'); }
    if (fd.includePhoneNumber !== false) { cols.push('Phone'); ex1.push('+15558010250'); ex2.push('+15558010251'); }
    (Array.isArray(fd.customFields) ? fd.customFields : []).filter(f => f && f.id && f.type !== 'message').forEach(f => {
        cols.push(f.label || f.id); ex1.push(''); ex2.push('');
    });
    _importSaveCsv([cols, ex1, ex2], 'quikrsvp-import-template.csv');
}
function _importSaveCsv(matrix, fileName) {
    const csv = matrix.map(r => r.map(_importCsvCell).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/**
 * Inject the "Import" button next to #add-guest-btn (avoids editing the 7
 * localized dashboard shells) and wire it. Idempotent.
 */
function wireImportResponsesButton() {
    const group = document.querySelector('#responses .responses-toolbar .toolbar-group') ||
        document.querySelector('.responses-toolbar .toolbar-group');
    if (!group) return;
    let btn = document.getElementById('import-responses-btn');
    if (!btn) {
        const t = getImportStrings();
        const addBtn = document.getElementById('add-guest-btn');
        btn = document.createElement('button');
        btn.id = 'import-responses-btn';
        btn.type = 'button';
        btn.className = 'btn btn-sm btn-outline-secondary';
        btn.title = t.title;
        btn.innerHTML = `<i class="bi bi-upload me-1"></i>${t.importBtn}`;
        if (addBtn && addBtn.parentNode === group) addBtn.insertAdjacentElement('afterend', btn);
        else group.appendChild(btn);
    }
    // "Add invitees" (task #881): people who have not replied yet, typed in -
    // no attending, meal or other answers to fill in, unlike Add Guest.
    if (!document.getElementById('add-invitees-btn')) {
        const t = getImportStrings();
        const inv = document.createElement('button');
        inv.id = 'add-invitees-btn';
        inv.type = 'button';
        inv.className = 'btn btn-sm btn-outline-secondary';
        inv.title = t.addInviteesTitle;
        inv.innerHTML = `<i class="bi bi-person-lines-fill me-1"></i>${t.addInviteesBtn}`;
        btn.insertAdjacentElement('afterend', inv);
        inv.addEventListener('click', (e) => { e.preventDefault(); openImportResponsesModal(null, { manual: true }); });
    }
    if (btn.dataset.wired === 'true') return;
    btn.dataset.wired = 'true';
    btn.addEventListener('click', (e) => { e.preventDefault(); openImportResponsesModal(); });
}

// Expose for console/testing
window.openImportResponsesModal = openImportResponsesModal;
